import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Chess } from 'chess.js';
import { db } from '../firebase/config';
import { doc, onSnapshot, updateDoc, increment } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { BOARD_THEMES, PIECE_SETS } from './Board/BoardConstants';
import { createEmptyTree, convertLegacyToTree, addMoveToTree, normalizeTree, goPreviousNode, goNextNode, goToRootNode, goToEndNode } from '../utils/ChessGameTree';

const PIECE_IMAGES = {
  'wR': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wR.svg', 'wN': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wN.svg',
  'wB': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wB.svg', 'wQ': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wQ.svg',
  'wK': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wK.svg', 'wP': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wP.svg',
  'bR': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bR.svg', 'bN': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bN.svg',
  'bB': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bB.svg', 'bQ': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bQ.svg',
  'bK': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bK.svg', 'bP': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bP.svg',
};

const STAR_ICON = 'https://upload.wikimedia.org/wikipedia/commons/2/29/Gold_Star.svg';
const CUTE_ITEMS = ['⭐', '🍎', '🍌', '🍉', '🍩', '🍦', '🎈', '🎁', '🧸', '💎', '🦄'];

const SOUNDS = { move: new Audio('https://raw.githubusercontent.com/ornicar/lila/master/public/sound/standard/Move.mp3'), capture: new Audio('https://raw.githubusercontent.com/ornicar/lila/master/public/sound/standard/Capture.mp3'), notify: new Audio('https://raw.githubusercontent.com/ornicar/lila/master/public/sound/standard/GenericNotify.mp3'), collect: new Audio('https://raw.githubusercontent.com/ornicar/lila/master/public/sound/standard/Victory.mp3') };
const playSound = (type) => { try { SOUNDS[type].currentTime = 0; SOUNDS[type].play().catch(()=>{}); } catch(e){} };
const getSquareCoords = (sq, isFlipped) => { const f = sq.charCodeAt(0) - 97; const r = parseInt(sq[1]) - 1; const cx = isFlipped ? (7 - f) * 12.5 + 6.25 : f * 12.5 + 6.25; const cy = isFlipped ? r * 12.5 + 6.25 : (7 - r) * 12.5 + 6.25; return { cx, cy }; };

const SOLID_COLOR_MAP = {
    green: 'rgb(34, 197, 94)',
    red: 'rgb(239, 68, 68)',
    blue: 'rgb(59, 130, 246)',
    yellow: 'rgb(234, 179, 8)',
    orange: 'rgb(249, 115, 22)'
};

const TRANSPARENT_COLOR_MAP = {
    green: 'rgba(34, 197, 94, 0.65)',
    red: 'rgba(239, 68, 68, 0.65)',
    blue: 'rgba(59, 130, 246, 0.65)',
    yellow: 'rgba(234, 179, 8, 0.65)',
    orange: 'rgba(249, 115, 22, 0.65)'
};

const getDrawColor = (e) => {
    if (!e) return { name: 'green', value: 'rgba(34, 197, 94, 0.65)' };
    if (e.ctrlKey && e.altKey) return { name: 'orange', value: 'rgba(249, 115, 22, 0.65)' };
    if (e.ctrlKey && e.shiftKey) return { name: 'orange', value: 'rgba(249, 115, 22, 0.65)' };
    if (e.shiftKey) return { name: 'red', value: 'rgba(239, 68, 68, 0.65)' };
    if (e.altKey) return { name: 'blue', value: 'rgba(59, 130, 246, 0.65)' };
    if (e.ctrlKey) return { name: 'yellow', value: 'rgba(234, 179, 8, 0.65)' };
    return { name: 'green', value: 'rgba(34, 197, 94, 0.65)' };
};

const safeLoadFen = (rawFen) => {
    if (!rawFen) return new Chess();
    let fen = rawFen.trim();
    const tokens = fen.split(' ');
    if (tokens.length >= 3 && (tokens[2] === '-' || !tokens[2])) {
        const pos = tokens[0];
        if (pos.includes('K') || pos.includes('k')) {
            let cRights = '';
            const tempTest = new Chess();
            try {
                tempTest.load(`${pos} w KQkq - 0 1`);
                const wK = tempTest.get('e1');
                if (wK && wK.type === 'k' && wK.color === 'w') {
                    const wRh = tempTest.get('h1');
                    const wRa = tempTest.get('a1');
                    if (wRh && wRh.type === 'r' && wRh.color === 'w') cRights += 'K';
                    if (wRa && wRa.type === 'r' && wRa.color === 'w') cRights += 'Q';
                }
                const bK = tempTest.get('e8');
                if (bK && bK.type === 'k' && bK.color === 'b') {
                    const bRh = tempTest.get('h8');
                    const bRa = tempTest.get('a8');
                    if (bRh && bRh.type === 'r' && bRh.color === 'b') cRights += 'k';
                    if (bRa && bRa.type === 'r' && bRa.color === 'b') cRights += 'q';
                }
            } catch (err) {}
            if (cRights) {
                tokens[2] = cRights;
                fen = tokens.join(' ');
            }
        }
    }
    const temp = new Chess();
    try { const valid = temp.load(fen); if (!valid) throw new Error("Strict Invalid"); return temp; } 
    catch (e) {
        temp.clear(); const ranks = tokens[0].split('/');
        ranks.forEach((row, rIdx) => {
            let cIdx = 0;
            for (let i = 0; i < row.length; i++) {
                const char = row[i];
                if (isNaN(char)) { const color = char === char.toLowerCase() ? 'b' : 'w'; temp.put({ type: char.toLowerCase(), color }, `${String.fromCharCode(97 + cIdx)}${8 - rIdx}`); cIdx++; } else { cIdx += parseInt(char); }
            }
        });
        const turn = tokens.length > 1 ? tokens[1] : 'w'; 
        const castling = (tokens.length > 2 && tokens[2] !== '-') ? tokens[2] : 'KQkq';
        const customFen = `${temp.fen().split(' ')[0]} ${turn} ${castling} - 0 1`;
        try { temp.load(customFen); } catch(e2) {} return temp;
    }
};

const getPseudoLegalMoves = (tempGame, sq, validTargets = []) => {
    let moves = tempGame.moves({ square: sq, verbose: true }).map(m => m.to);
    const piece = tempGame.get(sq); if (!piece) return moves;
    const cols = ['a','b','c','d','e','f','g','h']; const f = cols.indexOf(sq[0]); const r = parseInt(sq[1]);
    if (piece.type === 'p') { const dir = piece.color === 'w' ? 1 : -1; [-1, 1].forEach(df => { const targetSq = `${cols[f+df]}${r+dir}`; if (validTargets.includes(targetSq) && !moves.includes(targetSq)) moves.push(targetSq); }); }
    if (moves.length > 0) return moves;
    moves = []; 
    const checkDir = (df, dr, max) => { for (let i=1; i<=max; i++) { const nf = f + df*i; const nr = r + dr*i; if (nf<0 || nf>7 || nr<1 || nr>8) break; const tSq = `${cols[nf]}${nr}`; const tPiece = tempGame.get(tSq); if (!tPiece || validTargets.includes(tSq)) { moves.push(tSq); if(validTargets.includes(tSq)) break; } else { if (tPiece.color !== piece.color) moves.push(tSq); break; } } };
    if (piece.type === 'n') { [[1,2],[2,1],[-1,2],[-2,1],[1,-2],[2,-1],[-1,-2],[-2,-1]].forEach(d => { const nf = f+d[0]; const nr = r+d[1]; if(nf>=0 && nf<=7 && nr>=1 && nr<=8) { const targetSq = `${cols[nf]}${nr}`; const tp = tempGame.get(targetSq); if(!tp || tp.color !== piece.color || validTargets.includes(targetSq)) moves.push(targetSq); } }); }
    else if (piece.type === 'r') { [[0,1],[1,0],[0,-1],[-1,0]].forEach(d => checkDir(d[0], d[1], 7)); } else if (piece.type === 'b') { [[1,1],[1,-1],[-1,1],[-1,-1]].forEach(d => checkDir(d[0], d[1], 7)); } else if (piece.type === 'q') { [[0,1],[1,0],[0,-1],[-1,0],[1,1],[1,-1],[-1,1],[-1,-1]].forEach(d => checkDir(d[0], d[1], 7)); } else if (piece.type === 'k') {
        [[0,1],[1,0],[0,-1],[-1,0],[1,1],[1,-1],[-1,1],[-1,-1]].forEach(d => checkDir(d[0], d[1], 1));
        if (piece.color === 'w' && sq === 'e1') {
            if (!tempGame.get('f1') && !tempGame.get('g1') && tempGame.get('h1')?.type === 'r' && tempGame.get('h1')?.color === 'w') moves.push('g1');
            if (!tempGame.get('d1') && !tempGame.get('c1') && !tempGame.get('b1') && tempGame.get('a1')?.type === 'r' && tempGame.get('a1')?.color === 'w') moves.push('c1');
        }
        if (piece.color === 'b' && sq === 'e8') {
            if (!tempGame.get('f8') && !tempGame.get('g8') && tempGame.get('h8')?.type === 'r' && tempGame.get('h8')?.color === 'b') moves.push('g8');
            if (!tempGame.get('d8') && !tempGame.get('c8') && !tempGame.get('b8') && tempGame.get('a8')?.type === 'r' && tempGame.get('a8')?.color === 'b') moves.push('c8');
        }
    } else if (piece.type === 'p') { const dir = piece.color === 'w' ? 1 : -1; const startR = piece.color === 'w' ? 2 : 7; if (r+dir>=1 && r+dir<=8 && !tempGame.get(`${cols[f]}${r+dir}`)) { moves.push(`${cols[f]}${r+dir}`); if (r === startR && !tempGame.get(`${cols[f]}${r+dir*2}`)) moves.push(`${cols[f]}${r+dir*2}`); } [-1, 1].forEach(df => { if (f+df>=0 && f+df<=7 && r+dir>=1 && r+dir<=8) { const targetSq = `${cols[f+df]}${r+dir}`; const tp = tempGame.get(targetSq); if ((tp && tp.color !== piece.color) || validTargets.includes(targetSq)) moves.push(targetSq); } }); }
    return moves;
};

export default function ChessBoard({ role, classroomId, isFrozen, userEmail, rightPanelWidth, isIndividualMode, individualStudentEmail, isArenaMode, arenaGameId, boardWidth: propBoardWidth }) {
  const [game, setGame] = useState(new Chess());
  const [currentFen, setCurrentFen] = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
  const [boardItems, setBoardItems] = useState({}); 
  
  const [moveHistory, setMoveHistory] = useState([]);
  const [studentDots, setStudentDots] = useState(true);

  const [selectedSquare, setSelectedSquare] = useState(null); const [legalMoves, setLegalMoves] = useState([]);
  const [isFlipped, setIsFlipped] = useState(false); const [boardWidth, setBoardWidth] = useState(propBoardWidth || 600); 
  const prevPropBoardWidthRef = useRef(propBoardWidth); 
  const [highlights, setHighlights] = useState({}); const [arrows, setArrows] = useState([]); 
  const [drawStartSq, setDrawStartSq] = useState(null); const [undoStack, setUndoStack] = useState([]); const [redoStack, setRedoStack] = useState([]); 
  const [showCoords, setShowCoords] = useState(true); const [hidePieces, setHidePieces] = useState(false); const [moveAssist, setMoveAssist] = useState(true); const [allowIllegal, setAllowIllegal] = useState(false); const [floatingEmoji, setFloatingEmoji] = useState(null);
  const [showSquareCoords, setShowSquareCoords] = useState(false);
  const [freeMovement, setFreeMovement] = useState(false);
  const [classData, setClassData] = useState(null);
  const [showSetup, setShowSetup] = useState(false); const [setupBoard, setSetupBoard] = useState({}); const [setupTurn, setSetupTurn] = useState('w'); const [setupTool, setSetupTool] = useState(null);
  const [setupCastling, setSetupCastling] = useState({ K: true, Q: true, k: true, q: true });
  const [individualBoardActive, setIndividualBoardActive] = useState(false);
  const [variations, setVariations] = useState([]);
  const [moveComments, setMoveComments] = useState({});
  const [gameTree, setGameTree] = useState(() => createEmptyTree());
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextCommentInput, setContextCommentInput] = useState('');
  
  const [quickTool, setQuickTool] = useState(null);
  const [isDrawMode, setIsDrawMode] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showPieceFlyout, setShowPieceFlyout] = useState(false);
  const [draggingPiece, setDraggingPiece] = useState(null); 
  const [strictTurns, setStrictTurns] = useState(true);
  const [dropToDelete, setDropToDelete] = useState(false); // When ON: drag piece off board to delete it
  const [showPalette, setShowPalette] = useState(true); // Show/hide left pieces palette sidebar


  const handleDrawAction = async (from, to, e) => {
      const color = selectedColor 
          ? { name: selectedColor, value: TRANSPARENT_COLOR_MAP[selectedColor] } 
          : getDrawColor(e);
      let newHighlights = { ...highlights };
      let newArrows = [...arrows];
      
      if (from === to) {
          if (newHighlights[from]) {
              if (newHighlights[from] === color.value) {
                  delete newHighlights[from];
              } else {
                  newHighlights[from] = color.value;
              }
          } else {
              newHighlights[from] = color.value;
          }
      } else {
          const arrowPrefix = `${from}-${to}-`;
          const existingArrow = newArrows.find(a => a.startsWith(arrowPrefix));
          if (existingArrow) {
              if (existingArrow === `${from}-${to}-${color.name}`) {
                  newArrows = newArrows.filter(a => a !== existingArrow);
              } else {
                  newArrows = newArrows.filter(a => a !== existingArrow);
                  newArrows.push(`${from}-${to}-${color.name}`);
              }
          } else {
              const legacyArrow = `${from}-${to}`;
              if (newArrows.includes(legacyArrow)) {
                  newArrows = newArrows.filter(a => a !== legacyArrow);
                  newArrows.push(`${from}-${to}-${color.name}`);
              } else {
                  newArrows.push(`${from}-${to}-${color.name}`);
              }
          }
      }
      
      setHighlights(newHighlights);
      setArrows(newArrows);
      setDrawStartSq(null);
      setSelectedSquare(null);
      
      if (role === 'COACH') {
          await updateDoc(doc(db, "classes", classroomId), {
              boardHighlights: newHighlights,
              boardArrows: newArrows
          }).catch(err => console.error(err));
      }
  }; 

  const [boardTheme, setBoardTheme] = useState('blue');
  const [pieceTheme, setPieceTheme] = useState('cburnett');
  const [showToolsPanel, setShowToolsPanel] = useState(true);

  const isSetupDraggingRef = useRef(false);
  const setupDragInfo = useRef({
      piece: null,
      sourceSq: '',
      floatingEl: null,
      hasMoved: false,
      startX: 0,
      startY: 0
  });

  const handleSetupDragStart = (e, piece, sourceSq = '') => {
      if (e.button !== undefined && e.button !== 0) return; // Left click only
      
      const floating = document.createElement('img');
      floating.src = (PIECE_SETS[pieceTheme]?.[piece] || PIECE_IMAGES[piece]);
      floating.style.position = 'fixed';
      floating.style.width = '45px';
      floating.style.height = '45px';
      floating.style.pointerEvents = 'none';
      floating.style.zIndex = '10000';
      floating.style.left = `${e.clientX - 22}px`;
      floating.style.top = `${e.clientY - 22}px`;
      floating.style.opacity = '0.85';
      floating.style.transform = 'scale(1.15)';
      document.body.appendChild(floating);
      
      setupDragInfo.current = {
          piece,
          sourceSq,
          floatingEl: floating,
          hasMoved: false,
          startX: e.clientX,
          startY: e.clientY
      };
      
      document.addEventListener('pointermove', onSetupPointerMove);
      document.addEventListener('pointerup', onSetupPointerUp);
  };

  const onSetupPointerMove = (e) => {
      const info = setupDragInfo.current;
      if (!info.floatingEl) return;
      
      info.floatingEl.style.left = `${e.clientX - 22}px`;
      info.floatingEl.style.top = `${e.clientY - 22}px`;
      
      if (Math.abs(e.clientX - info.startX) > 5 || Math.abs(e.clientY - info.startY) > 5) {
          info.hasMoved = true;
      }
  };

  const onSetupPointerUp = (e) => {
      document.removeEventListener('pointermove', onSetupPointerMove);
      document.removeEventListener('pointerup', onSetupPointerUp);
      
      const { piece, sourceSq, floatingEl, hasMoved } = setupDragInfo.current;
      if (floatingEl) {
          floatingEl.remove();
      }
      
      if (hasMoved && piece) {
          isSetupDraggingRef.current = true;
          setTimeout(() => {
              isSetupDraggingRef.current = false;
          }, 50);
          
          const element = document.elementFromPoint(e.clientX, e.clientY);
          const squareEl = element?.closest('[data-setup-square]');
          const trashEl = element?.closest('[data-setup-trash]');
          
          const nb = { ...setupBoard };
          
          if (trashEl) {
              if (sourceSq) {
                  delete nb[sourceSq];
                  setSetupBoard(nb);
                  toast.success("Piece removed!");
              }
          } else if (squareEl) {
              const targetSq = squareEl.getAttribute('data-setup-square');
              if (sourceSq) {
                  delete nb[sourceSq];
              }
              nb[targetSq] = piece;
              setSetupBoard(nb);
          }
      }
      
      setupDragInfo.current = { piece: null, sourceSq: '', floatingEl: null, hasMoved: false, startX: 0, startY: 0 };
  };

  useEffect(() => {
    if (!userEmail) return;
    const unsub = onSnapshot(doc(db, "users", userEmail.toLowerCase()), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.boardTheme) setBoardTheme(data.boardTheme);
        if (data.pieceTheme) setPieceTheme(data.pieceTheme);
      }
    });
    return () => unsub();
  }, [userEmail]);

  const handleBoardThemeChange = async (val) => {
    setBoardTheme(val);
    if (userEmail) {
      await updateDoc(doc(db, "users", userEmail.toLowerCase()), { boardTheme: val }).catch(e => console.error(e));
    }
  };

  const handlePieceThemeChange = async (val) => {
    setPieceTheme(val);
    if (userEmail) {
      await updateDoc(doc(db, "users", userEmail.toLowerCase()), { pieceTheme: val }).catch(e => console.error(e));
    }
  }; 

  const [showFenModal, setShowFenModal] = useState(false); const [fenInput, setFenInput] = useState('');
  const [showPgnModal, setShowPgnModal] = useState(false); const [pgnInput, setPgnInput] = useState('');

  const [showQuizModal, setShowQuizModal] = useState(false); const [quizQues, setQuizQues] = useState('Find the best move'); const [quizAns, setQuizAns] = useState(''); const [quizTime, setQuizTime] = useState(60); const [quizPts, setQuizPts] = useState(10);
  const [quizTempGame, setQuizTempGame] = useState(new Chess()); const [quizSelectedSquare, setQuizSelectedSquare] = useState(null); const [quizLegalMoves, setQuizLegalMoves] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null); const [timeLeft, setTimeLeft] = useState(0); const [studentScores, setStudentScores] = useState({}); const [boardControl, setBoardControl] = useState({});
  const [isCheckingAnswer, setIsCheckingAnswer] = useState(false); const [isRecording, setIsRecording] = useState(false);
  const [quizStepIndex, setQuizStepIndex] = useState(0);
  const mediaRecorderRef = useRef(null); const recordedChunks = useRef([]);

  const parseSolutionSequence = (answers) => {
      if (!answers) return [];
      let rawStr = '';
      if (Array.isArray(answers)) {
          rawStr = answers.join(' ');
      } else {
          rawStr = String(answers);
      }
      rawStr = rawStr.replace(/\{[^}]*\}/g, '')
                     .replace(/\d+\.\.\.|\d+\./g, '')
                     .replace(/[!?]/g, '');
      const tokens = rawStr.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      return tokens;
  };

  const isMoveMatch = (moveObj, targetStr, currentBoardGame) => {
      if (!moveObj || !targetStr) return false;
      const cleanTarget = String(targetStr).replace(/^\d+\.\s*/, '').trim().toLowerCase();
      const moveSan = (moveObj.san || '').trim().toLowerCase();
      const moveLan = ((moveObj.from || '') + (moveObj.to || '')).trim().toLowerCase();
      const moveLanProm = (moveLan + (moveObj.promotion || '')).trim().toLowerCase();

      if (cleanTarget === moveSan || cleanTarget === moveLan || cleanTarget === moveLanProm) return true;
      if (cleanTarget.replace(/[+#x=]/g, '') === moveSan.replace(/[+#x=]/g, '')) return true;

      try {
          const testChess = safeLoadFen(currentBoardGame.fen());
          let targetObj = null;
          try { targetObj = testChess.move(cleanTarget); } catch(e){}
          if (!targetObj && cleanTarget.length >= 4) {
              try {
                  targetObj = testChess.move({
                      from: cleanTarget.substring(0, 2),
                      to: cleanTarget.substring(2, 4),
                      promotion: cleanTarget.length === 5 ? cleanTarget[4] : 'q'
                  });
              } catch(e){}
          }
          if (targetObj) {
              if (targetObj.from === moveObj.from && targetObj.to === moveObj.to) return true;
              if (targetObj.san.toLowerCase() === moveSan) return true;
          }
      } catch(e) {}

      return false;
  };

  useEffect(() => {
    if (!classroomId) return;
    const unsub = onSnapshot(doc(db, "classes", classroomId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        let fenToUse = data.currentFen;
        let isSolo = false;
        
        if (isArenaMode && arenaGameId) {
            const gameData = data.activeGames?.[arenaGameId];
            if (gameData) {
                fenToUse = gameData.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
                if (gameData.pgn && !isCheckingAnswer) {
                    const moves = gameData.pgn.split(',').filter(Boolean);
                    if (JSON.stringify(moves) !== JSON.stringify(moveHistory)) {
                        setMoveHistory(moves);
                    }
                }
            }
        } else if (role === 'STUDENT' && userEmail) {
            const safeEmail = userEmail.replace(/\./g, '_');
            const studentSettings = data.individualBoards?.[safeEmail];
            if (studentSettings?.active) {
                isSolo = true;
                fenToUse = studentSettings.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
            } else if (data.quizData?.active) {
                fenToUse = data.studentFens?.[safeEmail] || data.currentFen;
            }
        } else if (isIndividualMode && individualStudentEmail) {
            const safeEmail = individualStudentEmail.replace(/\./g, '_');
            const studentSettings = data.individualBoards?.[safeEmail];
            if (studentSettings?.active) {
                isSolo = true;
                fenToUse = studentSettings.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
            }
        }
        
        setIndividualBoardActive(isSolo);

        if (fenToUse && fenToUse !== currentFen && !isCheckingAnswer) {
            const temp = safeLoadFen(fenToUse);
            setGame(temp);
            setCurrentFen(fenToUse);
            playSound('move');
        }

        setClassData(data);
        setHighlights(data.boardHighlights || {}); setArrows(data.boardArrows || []); 
        setBoardItems(data.boardItems || {}); 
        if (!isArenaMode && !isSolo && data.moveHistory !== undefined) {
            if (JSON.stringify(data.moveHistory) !== JSON.stringify(moveHistory)) {
                setMoveHistory(data.moveHistory); 
            }
        }
        if (data.gameTree !== undefined) {
            setGameTree(data.gameTree);
        } else if (data.moveHistory !== undefined) {
            const converted = convertLegacyToTree(data.moveHistory, data.variations || [], data.moveComments || {});
            setGameTree(converted);
        }
        if (data.variations !== undefined) setVariations(data.variations);
        if (data.moveComments !== undefined) setMoveComments(data.moveComments);
        if (data.hidePieces !== undefined) setHidePieces(data.hidePieces); if (data.moveAssist !== undefined) setMoveAssist(data.moveAssist); if (data.allowIllegal !== undefined) setAllowIllegal(data.allowIllegal); if (data.boardControl !== undefined) setBoardControl(data.boardControl);
        if (data.strictTurns !== undefined) setStrictTurns(data.strictTurns);
        if (data.showSquareCoords !== undefined) setShowSquareCoords(data.showSquareCoords);
        if (data.freeMovement !== undefined) setFreeMovement(data.freeMovement);
        if (data.emojiEvent && data.emojiEvent.time > Date.now() - 3000) { setFloatingEmoji(data.emojiEvent.emoji); playSound('notify'); setTimeout(() => setFloatingEmoji(null), 2000); }
        setActiveQuiz(data.quizData || null); setStudentScores(data.studentScores || {});
      }
    });
    return () => unsub();
  }, [classroomId, currentFen, isCheckingAnswer, role, userEmail, isIndividualMode, individualStudentEmail]);

  useEffect(() => {
      if (!activeQuiz?.active || !activeQuiz.endTime) return;
      const interval = setInterval(() => { const remaining = Math.max(0, Math.floor((activeQuiz.endTime - Date.now()) / 1000)); setTimeLeft(remaining); if (remaining <= 0 && role === 'COACH') updateDoc(doc(db, "classes", classroomId), { 'quizData.active': false, classMode: 'PRACTICE' }); }, 1000);
      return () => clearInterval(interval);
  }, [activeQuiz, role, classroomId]);

  const syncBoard = async (tempGame, clearGraphics = true, isHistoryMove = false, newHistory = null) => {
      let histToSave = newHistory !== null ? newHistory : moveHistory;
      let updatedTree = gameTree;

      if (!isHistoryMove) { 
          const moveVerbose = tempGame.history({ verbose: true });
          const playedSan = moveVerbose.length > 0 ? moveVerbose[moveVerbose.length - 1].san : null;
          const lastMoveObj = moveVerbose.length > 0 ? moveVerbose[moveVerbose.length - 1] : null;

          if (lastMoveObj) {
              const previousFen = game.fen();
              updatedTree = addMoveToTree(gameTree, lastMoveObj, tempGame.fen(), previousFen);
              setGameTree(updatedTree);
          }

          const effIdx = currentMoveIndex >= 0 ? currentMoveIndex : (moveHistory.length > 0 ? moveHistory.length - 1 : -1);

          if (playedSan && effIdx < moveHistory.length - 1) {
              const branchPly = effIdx + 1;
              const newVar = {
                  moveIndex: branchPly,
                  moves: [playedSan]
              };
              const updatedVars = [...variations, newVar];
              setVariations(updatedVars);
              setCurrentMoveIndex(effIdx);

              if (role === 'COACH' && classroomId) {
                  updateDoc(doc(db, "classes", classroomId), { variations: updatedVars, gameTree: updatedTree }).catch(() => {});
              }
              const isBlackVar = branchPly % 2 === 1;
              const moveNum = Math.floor(branchPly / 2) + 1;
              const moveStr = isBlackVar ? `${moveNum}... ${playedSan}` : `${moveNum}. ${playedSan}`;
              toast.success(`Sub-variation added: ( ${moveStr} ) 🌿`);
          } else if (playedSan) {
              const updatedHist = [...moveHistory, playedSan];
              setMoveHistory(updatedHist);
              setCurrentMoveIndex(updatedHist.length - 1);
              histToSave = updatedHist;
          }
      }

      setGame(tempGame); setCurrentFen(tempGame.fen());
      if (newHistory !== null) setMoveHistory(newHistory);

      if (isArenaMode && arenaGameId) {
          const pgnString = histToSave.join(',');
          await updateDoc(doc(db, "classes", classroomId), {
              [`activeGames.${arenaGameId}.fen`]: tempGame.fen(),
              [`activeGames.${arenaGameId}.pgn`]: pgnString,
              [`activeGames.${arenaGameId}.turn`]: tempGame.turn(),
              [`activeGames.${arenaGameId}.lastMoveAt`]: Date.now()
          });
      } else if (individualBoardActive) {
          const targetEmail = (role === 'STUDENT' ? userEmail : individualStudentEmail);
          if (targetEmail) {
              const safeEmail = targetEmail.replace(/\./g, '_');
              await updateDoc(doc(db, "classes", classroomId), {
                  [`individualBoards.${safeEmail}.fen`]: tempGame.fen(),
                  [`individualBoards.${safeEmail}.moveHistory`]: histToSave,
                  [`studentFens.${safeEmail}`]: tempGame.fen()
              });
          }
      } else {
          const updates = { currentFen: tempGame.fen(), moveHistory: histToSave, gameTree: updatedTree }; 
          if (clearGraphics) { updates.boardHighlights = {}; updates.boardArrows = []; }
          const studentCanSync = role === 'STUDENT' && (!isFrozen || boardControl[userEmail]);
          if (role === 'COACH' || studentCanSync) {
              await updateDoc(doc(db, "classes", classroomId), updates);
          }
      }
  };

  const handlePlaylistPrev = async () => {
      if (!classData || !classData.playlistItems || classData.playlistIndex === undefined) return;
      const newIndex = classData.playlistIndex - 1;
      if (newIndex < 0) return;
      
      const item = classData.playlistItems[newIndex];
      const t = new Chess();
      let newFen = '';
      let newHistory = [];
      if (item.type === 'pgn') {
          try { t.loadPgn(item.content); } catch(e){}
          newFen = t.fen();
          newHistory = t.history();
      } else {
          try { t.load(item.content); } catch(e){}
          newFen = t.fen();
      }
      
      await updateDoc(doc(db, "classes", classroomId), {
          playlistIndex: newIndex,
          currentFen: newFen,
          moveHistory: newHistory,
          boardHighlights: {},
          boardArrows: [],
          boardItems: {},
          studentFens: {}
      });
      toast.success(`Loaded: ${item.title}`);
  };

  const handlePlaylistNext = async () => {
      if (!classData || !classData.playlistItems || classData.playlistIndex === undefined) return;
      const newIndex = classData.playlistIndex + 1;
      if (newIndex >= classData.playlistItems.length) return;
      
      const item = classData.playlistItems[newIndex];
      const t = new Chess();
      let newFen = '';
      let newHistory = [];
      if (item.type === 'pgn') {
          try { t.loadPgn(item.content); } catch(e){}
          newFen = t.fen();
          newHistory = t.history();
      } else {
          try { t.load(item.content); } catch(e){}
          newFen = t.fen();
      }
      
      await updateDoc(doc(db, "classes", classroomId), {
          playlistIndex: newIndex,
          currentFen: newFen,
          moveHistory: newHistory,
          boardHighlights: {},
          boardArrows: [],
          boardItems: {},
          studentFens: {}
      });
      toast.success(`Loaded: ${item.title}`);
  };

  const handleLaunchPlaylistQuiz = async () => {
      if (!classData || !classData.playlistItems || classData.playlistIndex === undefined) return;
      const currentItem = classData.playlistItems[classData.playlistIndex];
      if (!currentItem || currentItem.type !== 'puzzle') return;
      const ansStr = currentItem.answer || '';
      const answerArray = ansStr.split(',').map(s => s.trim()).filter(Boolean);
      if (answerArray.length === 0) {
          toast.error("This puzzle has no solution moves saved!");
          return;
      }
      
      const quizTimeVal = 60; // 60s default
      const quizPtsVal = currentItem.points || 10;
      
      await updateDoc(doc(db, "classes", classroomId), {
          classMode: 'QUIZ',
          studentFens: {},
          quizData: {
              question: "Find the best move",
              answers: answerArray,
              endTime: Date.now() + (quizTimeVal * 1000),
              points: parseInt(quizPtsVal),
              active: true,
              solvedBy: []
          }
      });
      toast.success("Puzzle Launched as Quiz! 🚀");
  };

  const handleClearPlaylist = async () => {
      if (!classroomId) return;
      try {
          await updateDoc(doc(db, "classes", classroomId), {
              playlistItems: [],
              playlistIndex: 0
          });
          toast.success("Playlist dismissed.");
      } catch (e) {
          console.error("Failed to clear playlist:", e);
          toast.error("Could not clear playlist.");
      }
  };

  const canStudentBrowse = role === 'COACH';

  const jumpToPath = useCallback(async (pathObj) => {
      const startFen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      const temp = safeLoadFen(startFen);

      if (pathObj.type === 'main') {
          const targetPly = pathObj.ply;
          for (let k = 0; k <= targetPly; k++) {
              const item = moveHistory[k];
              const san = typeof item === 'object' ? item.san : String(item || '');
              try { temp.move(san); } catch(e) {}
          }
          setCurrentMoveIndex(targetPly);
          syncBoard(temp, true, true, moveHistory);
          if (role === 'COACH' && classroomId) {
              updateDoc(doc(db, "classes", classroomId), {
                  currentMoveIndex: targetPly,
                  activePath: pathObj
              }).catch(() => {});
          }
      } else if (pathObj.type === 'var') {
          const targetVar = variations[pathObj.varIdx];
          if (!targetVar) return;
          const branchPly = targetVar.moveIndex;

          for (let k = 0; k < branchPly; k++) {
              const item = moveHistory[k];
              const san = typeof item === 'object' ? item.san : String(item || '');
              try { temp.move(san); } catch(e) {}
          }
          const varMoves = targetVar.moves || [];
          for (let k = 0; k <= pathObj.moveIdx; k++) {
              const san = typeof varMoves[k] === 'object' ? varMoves[k].san : String(varMoves[k] || '');
              try { temp.move(san); } catch(e) {}
          }
          setCurrentMoveIndex(branchPly + pathObj.moveIdx);
          syncBoard(temp, true, true, moveHistory);
          if (role === 'COACH' && classroomId) {
              updateDoc(doc(db, "classes", classroomId), {
                  currentMoveIndex: branchPly + pathObj.moveIdx,
                  activePath: pathObj
              }).catch(() => {});
          }
      }
  }, [moveHistory, variations, safeLoadFen, syncBoard, role, classroomId]);

  const jumpToMoveIndex = (targetIndex) => {
      jumpToPath({ type: 'main', ply: targetIndex });
  };

  const handleMoveRightClick = (e, moveIdx) => {
      e.preventDefault();
      const existingMove = moveHistory[moveIdx];
      const san = typeof existingMove === 'object' ? existingMove.san : String(existingMove || '');
      const nag = typeof existingMove === 'object' ? existingMove.nag : null;
      const comment = moveComments[moveIdx] || '';
      
      setContextCommentInput(comment);
      setContextMenu({
          x: e.clientX,
          y: e.clientY,
          moveIndex: moveIdx,
          san,
          nag,
          comment
      });
  };

  const handleAddComment = async (moveIndex, commentStr) => {
      const updatedComments = { ...moveComments, [moveIndex]: commentStr };
      setMoveComments(updatedComments);
      if (role === 'COACH' && classroomId) {
          await updateDoc(doc(db, "classes", classroomId), { moveComments: updatedComments });
          toast.success("Comment added to move! 💬");
      }
  };

  const handleAddNag = async (moveIndex, nagSymbol) => {
      const updatedMoves = [...moveHistory];
      const existing = updatedMoves[moveIndex];
      const san = typeof existing === 'object' ? existing.san : String(existing);
      const currentNag = typeof existing === 'object' ? existing.nag : null;
      const newNag = currentNag === nagSymbol ? null : nagSymbol;
      
      updatedMoves[moveIndex] = { san, nag: newNag };
      setMoveHistory(updatedMoves);
      if (role === 'COACH' && classroomId) {
          await updateDoc(doc(db, "classes", classroomId), { moveHistory: updatedMoves });
          toast.success(`Move marked as ${newNag || 'normal'}`);
      }
  };

  const activePath = classData?.activePath || null;

  const navigateTree = useCallback((navFn) => {
      if (!canStudentBrowse) return;
      const norm = normalizeTree(gameTree);
      const updated = navFn(norm);
      setGameTree(updated);

      const currNode = updated.nodes[updated.currentNodeId];
      if (currNode && currNode.fen) {
          const temp = safeLoadFen(currNode.fen);
          setGame(temp);
          setCurrentFen(currNode.fen);
          playSound('move');

          const studentCanSync = role === 'STUDENT' && (!isFrozen || boardControl[userEmail]);
          if ((role === 'COACH' || studentCanSync) && classroomId) {
              updateDoc(doc(db, "classes", classroomId), {
                  gameTree: updated,
                  currentFen: currNode.fen
              }).catch(() => {});
          }
      }
  }, [canStudentBrowse, gameTree, role, isFrozen, boardControl, userEmail, classroomId]);

  const stepBackward = useCallback(() => navigateTree(goPreviousNode), [navigateTree]);
  const stepForward = useCallback(() => navigateTree(goNextNode), [navigateTree]);
  const fastBackward = useCallback(() => navigateTree(goToRootNode), [navigateTree]);
  const fastForward = useCallback(() => navigateTree(goToEndNode), [navigateTree]);

  const eraseNotation = useCallback(() => { setRedoStack([]); toast.success("Forward notation erased!"); }, []);

  useEffect(() => {
    const handleKeyDown = (e) => { 
        if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;
        if (!canStudentBrowse) return;
        if (e.key === 'ArrowLeft') stepBackward(); 
        if (e.key === 'ArrowRight') stepForward(); 
        if (e.key === 'ArrowUp' || e.key === 'Home') fastBackward(); 
        if (e.key === 'ArrowDown' || e.key === 'End') fastForward(); 
        if (e.key === 'Escape') eraseNotation(); 
    };
    window.addEventListener('keydown', handleKeyDown); return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canStudentBrowse, stepBackward, stepForward, fastBackward, fastForward, eraseNotation]);

  useEffect(() => {
      if (propBoardWidth !== prevPropBoardWidthRef.current) {
          setBoardWidth(propBoardWidth);
          prevPropBoardWidthRef.current = propBoardWidth;
      }
  }, [propBoardWidth]);

  useEffect(() => {
      if (propBoardWidth) return;
      const handleResize = () => {
          const vh = window.innerHeight;
          const vw = window.innerWidth;
          const desktop = vw > 1024;
          let optimalSize;
          const subtractH = (role === 'COACH') ? 170 : 160;
          // Account for the 125px left sidebar when coach is in normal (non-individual) mode
          const sidebarOffset = (role === 'COACH' && !isIndividualMode && desktop) ? 125 : 0;
          if (desktop) {
              const verticalAvailable = vh - subtractH;
              const padW = (role === 'COACH') ? 120 : 100;
              const wToSubtract = rightPanelWidth ? (rightPanelWidth + padW) : 620;
              const layoutSidebarWidth = (vw > 1100) ? 260 : 0;
              const horizontalAvailable = vw - wToSubtract - sidebarOffset - layoutSidebarWidth;
              optimalSize = Math.min(verticalAvailable, horizontalAvailable);
          } else {
              const subtractMobile = (role === 'COACH') ? 480 : 380;
              const verticalAvailable = vh - subtractMobile;
              const horizontalAvailable = vw - 30;
              optimalSize = Math.min(verticalAvailable, horizontalAvailable);
          }
          optimalSize = Math.max(280, optimalSize);
          setBoardWidth(optimalSize);
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
  }, [role, rightPanelWidth, propBoardWidth, isIndividualMode]);

  const startResize = useCallback((e) => {
      e.preventDefault();
      const startX = e.touches ? e.touches[0].clientX : e.clientX;
      const startWidth = boardWidth;
      
      const doDrag = (dragEvent) => { 
          const currentX = dragEvent.touches ? dragEvent.touches[0].clientX : dragEvent.clientX;
          if (currentX === undefined) return;
          const deltaX = currentX - startX;
          
          let maxPossibleWidth = 1400;
          const subtractH = (role === 'COACH') ? 170 : 160;
          const sidebarOffset = (role === 'COACH' && !isIndividualMode && window.innerWidth > 1024) ? 125 : 0;
          if (window.innerWidth > 1024) {
              const padW = (role === 'COACH') ? 120 : 100;
              const wToSubtract = rightPanelWidth ? (rightPanelWidth + padW) : 620;
              const layoutSidebarWidth = (window.innerWidth > 1100) ? 260 : 0;
              maxPossibleWidth = Math.min(window.innerWidth - wToSubtract - sidebarOffset - layoutSidebarWidth, window.innerHeight - subtractH);
          } else {
              maxPossibleWidth = Math.min(window.innerWidth - 40, window.innerHeight - 260);
          }
          maxPossibleWidth = Math.max(280, maxPossibleWidth);
          setBoardWidth(Math.max(280, Math.min(maxPossibleWidth, startWidth + deltaX))); 
      };
      
      const stopDrag = () => { 
          window.removeEventListener('pointermove', doDrag); 
          window.removeEventListener('pointerup', stopDrag); 
          window.removeEventListener('touchmove', doDrag);
          window.removeEventListener('touchend', stopDrag);
      };
      
      window.addEventListener('pointermove', doDrag); 
      window.addEventListener('pointerup', stopDrag);
      window.addEventListener('touchmove', doDrag, { passive: false });
      window.addEventListener('touchend', stopDrag);
  }, [boardWidth, role, rightPanelWidth, isIndividualMode]);

  const startResizeFromEdge = useCallback((edge) => (e) => {
      e.preventDefault();
      const startX = e.touches ? e.touches[0].clientX : e.clientX;
      const startY = e.touches ? e.touches[0].clientY : e.clientY;
      const startWidth = boardWidth;
      
      const doDrag = (dragEvent) => { 
          const currentX = dragEvent.touches ? dragEvent.touches[0].clientX : dragEvent.clientX;
          const currentY = dragEvent.touches ? dragEvent.touches[0].clientY : dragEvent.clientY;
          if (currentX === undefined || currentY === undefined) return;

          let delta = 0;
          if (edge === 'right') delta = currentX - startX;
          else if (edge === 'left') delta = startX - currentX;
          else if (edge === 'bottom') delta = currentY - startY;
          else if (edge === 'top') delta = startY - currentY;
          
          let maxPossibleWidth = 1400;
          const subtractH = (role === 'COACH') ? 170 : 160;
          const sidebarOffset = (role === 'COACH' && !isIndividualMode && window.innerWidth > 1024) ? 125 : 0;
          if (window.innerWidth > 1024) {
              const padW = (role === 'COACH') ? 120 : 100;
              const wToSubtract = rightPanelWidth ? (rightPanelWidth + padW) : 620;
              const layoutSidebarWidth = (window.innerWidth > 1100) ? 260 : 0;
              maxPossibleWidth = Math.min(window.innerWidth - wToSubtract - sidebarOffset - layoutSidebarWidth, window.innerHeight - subtractH);
          } else {
              maxPossibleWidth = Math.min(window.innerWidth - 40, window.innerHeight - 260);
          }
          maxPossibleWidth = Math.max(280, maxPossibleWidth);
          setBoardWidth(Math.max(280, Math.min(maxPossibleWidth, startWidth + delta))); 
      };
      
      const stopDrag = () => { 
          window.removeEventListener('pointermove', doDrag); 
          window.removeEventListener('pointerup', stopDrag); 
          window.removeEventListener('touchmove', doDrag);
          window.removeEventListener('touchend', stopDrag);
      };
      
      window.addEventListener('pointermove', doDrag); 
      window.addEventListener('pointerup', stopDrag);
      window.addEventListener('touchmove', doDrag, { passive: false });
      window.addEventListener('touchend', stopDrag);
  }, [boardWidth, role, rightPanelWidth, isIndividualMode]);

  const checkItemCollection = (targetSq) => {
      if (boardItems[targetSq]) {
          playSound('collect');
          const newItems = { ...boardItems }; delete newItems[targetSq];
          setBoardItems(newItems); updateDoc(doc(db, "classes", classroomId), { boardItems: newItems });
      }
  };

  const attemptMove = async (from, to) => {
    if (isCheckingAnswer) return;
    if (isArenaMode && arenaGameId) {
        const currentTurn = game.turn();
        const myColor = role === 'WHITE' ? 'w' : (role === 'BLACK' ? 'b' : null);
        if (myColor !== null) {
            const piece = game.get(from);
            if (piece && piece.color !== myColor) return;
            if (currentTurn !== myColor) return;
        } else if (role === 'SPECTATOR') {
            return;
        }
    }
    const effectiveIsFrozen = individualBoardActive ? false : isFrozen;
    if (role === 'STUDENT' && effectiveIsFrozen && !activeQuiz?.active && !allowIllegal && !freeMovement) { const ctrl = boardControl[userEmail]; if (!ctrl) return; const tempCheck = safeLoadFen(currentFen); const piece = tempCheck.get(from); if (piece && ctrl !== 'both' && piece.color !== ctrl) return; }
    if (freeMovement) {
        try {
            let temp = safeLoadFen(currentFen);
            const pieceToMove = temp.get(from);
            if (pieceToMove) {
                temp.remove(from);
                temp.put({ type: pieceToMove.type, color: pieceToMove.color }, to);
                const tokens = temp.fen().split(' ');
                tokens[1] = pieceToMove.color === 'w' ? 'b' : 'w';
                const nextTemp = safeLoadFen(tokens.join(' '));
                playSound('move');
                const sanMove = `${pieceToMove.type.toUpperCase() === 'P' ? '' : pieceToMove.type.toUpperCase()}@${to}`;
                syncBoard(nextTemp, true, false, [...moveHistory, sanMove]);
                checkItemCollection(to);
            }
        } catch (e) {
            console.error("Free movement error:", e);
        }
        setSelectedSquare(null);
        setLegalMoves([]);
        return;
    }

    try {
        let temp = safeLoadFen(currentFen);
        const validTargets = Object.keys(boardItems);

        if (role === 'STUDENT' && activeQuiz?.active) {
            const move = temp.move({ from, to, promotion: 'q' });
            if (move) {
                if (activeQuiz.solvedBy?.includes(userEmail)) { toast.error("You already solved this puzzle!"); setSelectedSquare(null); setLegalMoves([]); return; }
                const safeEmail = userEmail.replace(/\./g, '_');
                const currentAttempts = classData?.quizAttempts?.[safeEmail] || [];
                
                const sequence = parseSolutionSequence(activeQuiz.answers);
                const targetMoveStr = sequence[quizStepIndex] || sequence[0];
                const isStepCorrect = isMoveMatch(move, targetMoveStr, temp);

                if (isStepCorrect) {
                    const nextStepIndex = quizStepIndex + 1;
                    const updatedAttempts = [...currentAttempts, { move: move.san, correct: true }];

                    if (nextStepIndex >= sequence.length) {
                        playSound('notify');
                        toast.success(`Correct Move! +${activeQuiz.points} XP! 🎉`);
                        setGame(temp);
                        setCurrentFen(temp.fen());
                        setQuizStepIndex(0);
                        await updateDoc(doc(db, "classes", classroomId), { 
                            'quizData.solvedBy': [...(activeQuiz.solvedBy || []), userEmail], 
                            [`studentFens.${safeEmail}`]: temp.fen(),
                            [`quizAttempts.${safeEmail}`]: updatedAttempts
                        });
                    } else {
                        playSound('move');
                        setGame(temp);
                        setCurrentFen(temp.fen());
                        await updateDoc(doc(db, "classes", classroomId), {
                            [`studentFens.${safeEmail}`]: temp.fen(),
                            [`quizAttempts.${safeEmail}`]: updatedAttempts
                        });

                        const opponentMoveStr = sequence[nextStepIndex];
                        if (opponentMoveStr) {
                            toast.success("Good move! Keep going... 🧠", { duration: 2000 });
                            setQuizStepIndex(nextStepIndex + 1);

                            setTimeout(async () => {
                                try {
                                    const oppGame = safeLoadFen(temp.fen());
                                    let oppMove = null;
                                    try { oppMove = oppGame.move(opponentMoveStr); } catch(e){}
                                    if (!oppMove && opponentMoveStr.length >= 4) {
                                        try {
                                            oppMove = oppGame.move({
                                                from: opponentMoveStr.substring(0, 2),
                                                to: opponentMoveStr.substring(2, 4),
                                                promotion: opponentMoveStr.length === 5 ? opponentMoveStr[4] : 'q'
                                            });
                                        } catch(e){}
                                    }

                                    if (oppMove) {
                                        playSound(oppMove.captured ? 'capture' : 'move');
                                        setGame(oppGame);
                                        setCurrentFen(oppGame.fen());
                                        await updateDoc(doc(db, "classes", classroomId), {
                                            [`studentFens.${safeEmail}`]: oppGame.fen()
                                        });

                                        if (nextStepIndex + 1 >= sequence.length) {
                                            playSound('notify');
                                            toast.success(`Puzzle Solved! +${activeQuiz.points} XP! 🎉`);
                                            setQuizStepIndex(0);
                                            await updateDoc(doc(db, "classes", classroomId), {
                                                'quizData.solvedBy': [...(activeQuiz.solvedBy || []), userEmail]
                                            });
                                        }
                                    }
                                } catch (err) {
                                    console.error("Opponent move play error:", err);
                                }
                            }, 500);
                        } else {
                            setQuizStepIndex(nextStepIndex);
                        }
                    }
                } else {
                    playSound('capture');
                    toast.error("Wrong Move! Try again! ❌");
                    setIsCheckingAnswer(true);
                    setGame(temp);
                    setCurrentFen(temp.fen()); 
                    const updatedAttempts = [...currentAttempts, { move: move.san, correct: false }];
                    await updateDoc(doc(db, "classes", classroomId), { 
                        [`studentFens.${safeEmail}`]: temp.fen(),
                        [`quizAttempts.${safeEmail}`]: updatedAttempts
                    });
                    setTimeout(() => {
                        const revertFen = classData?.currentFen || currentFen;
                        const revertGame = safeLoadFen(revertFen);
                        setGame(revertGame);
                        setCurrentFen(revertFen);
                        setQuizStepIndex(0);
                        updateDoc(doc(db, "classes", classroomId), { [`studentFens.${safeEmail}`]: revertFen });
                        setIsCheckingAnswer(false);
                    }, 1800);
                }
            }
            setSelectedSquare(null); setLegalMoves([]); return; 
        }

        const pieceToMove = temp.get(from);
        if (!pieceToMove) return;

        if (!strictTurns || allowIllegal || freeMovement) {
            const tokens = temp.fen().split(' '); tokens[1] = pieceToMove.color; temp = safeLoadFen(tokens.join(' '));
        }

        let move = temp.move({ from, to, promotion: 'q' });
        let customPawnCapture = false;
        
        if (!move && pieceToMove.type === 'p' && validTargets.includes(to)) {
            const cols = ['a','b','c','d','e','f','g','h'];
            const fFrom = cols.indexOf(from[0]); const rFrom = parseInt(from[1]);
            const fTo = cols.indexOf(to[0]); const rTo = parseInt(to[1]);
            const dir = pieceToMove.color === 'w' ? 1 : -1;
            if (Math.abs(fFrom - fTo) === 1 && rTo - rFrom === dir) customPawnCapture = true;
        }

        if (move || customPawnCapture || allowIllegal || freeMovement) {
            if (customPawnCapture || allowIllegal || freeMovement) {
                temp = safeLoadFen(currentFen); temp.remove(from); temp.put(pieceToMove, to);
                if (pieceToMove.type === 'k') {
                    if (from === 'e1' && to === 'g1') { temp.remove('h1'); temp.put({ type: 'r', color: 'w' }, 'f1'); }
                    if (from === 'e1' && to === 'c1') { temp.remove('a1'); temp.put({ type: 'r', color: 'w' }, 'd1'); }
                    if (from === 'e8' && to === 'g8') { temp.remove('h8'); temp.put({ type: 'r', color: 'b' }, 'f8'); }
                    if (from === 'e8' && to === 'c8') { temp.remove('a8'); temp.put({ type: 'r', color: 'b' }, 'd8'); }
                }
                const tokens = temp.fen().split(' '); tokens[1] = pieceToMove.color === 'w' ? 'b' : 'w';
                temp = safeLoadFen(tokens.join(' '));
            }
            playSound(move?.captured || customPawnCapture ? 'capture' : 'move'); 
            
            const sanMove = move ? move.san : (customPawnCapture ? `x${to}` : `${pieceToMove.type.toUpperCase()==='P'?'':pieceToMove.type.toUpperCase()}@${to}`);
            syncBoard(temp, true, false, [...moveHistory, sanMove]);
            
            checkItemCollection(to); 
        } else if (!strictTurns) {
            const pseudoMoves = getPseudoLegalMoves(temp, from, validTargets);
            if (pseudoMoves.includes(to)) {
                temp.remove(from); temp.put(pieceToMove, to);
                if (pieceToMove.type === 'k') {
                    if (from === 'e1' && to === 'g1') { temp.remove('h1'); temp.put({ type: 'r', color: 'w' }, 'f1'); }
                    if (from === 'e1' && to === 'c1') { temp.remove('a1'); temp.put({ type: 'r', color: 'w' }, 'd1'); }
                    if (from === 'e8' && to === 'g8') { temp.remove('h8'); temp.put({ type: 'r', color: 'b' }, 'f8'); }
                    if (from === 'e8' && to === 'c8') { temp.remove('a8'); temp.put({ type: 'r', color: 'b' }, 'd8'); }
                }
                const tokens = temp.fen().split(' '); tokens[1] = pieceToMove.color === 'w' ? 'b' : 'w';
                const nextTemp = safeLoadFen(tokens.join(' '));
                playSound('move');
                const sanMove = (from === 'e1' && (to === 'g1' || to === 'c1')) || (from === 'e8' && (to === 'g8' || to === 'c8'))
                    ? (to[0] === 'g' ? 'O-O' : 'O-O-O')
                    : `${pieceToMove.type.toUpperCase()==='P'?'':pieceToMove.type.toUpperCase()}@${to}`;
                syncBoard(nextTemp, true, false, [...moveHistory, sanMove]);
                checkItemCollection(to); 
            }
        }
    } catch (e) {}
    setSelectedSquare(null); setLegalMoves([]);
  };

  const handlePointerDown = (e, id) => { if (role === 'COACH' && e.button === 2) setDrawStartSq(id); };
  const handlePointerUp = async (e, id) => { 
      if (role === 'COACH' && e.button === 2 && drawStartSq) { 
          handleDrawAction(drawStartSq, id, e);
      } 
  };

  const handleClick = (id) => {
    if (isCheckingAnswer) return;
    const effectiveIsFrozen = individualBoardActive ? false : isFrozen;
    if (role === 'STUDENT' && effectiveIsFrozen && !activeQuiz?.active && !allowIllegal) { const ctrl = boardControl[userEmail]; if (!ctrl) return; }
    
    if (role === 'COACH' && quickTool) {
        if (CUTE_ITEMS.includes(quickTool)) {
            const newItems = { ...boardItems, [id]: quickTool }; setBoardItems(newItems); updateDoc(doc(db, "classes", classroomId), { boardItems: newItems });
        } else {
            let itemErased = false;
            if (quickTool === 'eraser' && boardItems[id]) { const newItems = { ...boardItems }; delete newItems[id]; setBoardItems(newItems); updateDoc(doc(db, "classes", classroomId), { boardItems: newItems }); itemErased = true; }
            const temp = safeLoadFen(currentFen);
            if (quickTool === 'eraser') { temp.remove(id); } else { temp.put({ type: quickTool[1].toLowerCase(), color: quickTool[0] }, id); }
            if (!itemErased || temp.get(id)) syncBoard(temp, false); 
        } return;
    }

    if (role === 'COACH' && isDrawMode) {
        if (!drawStartSq) { setDrawStartSq(id); setSelectedSquare(id); } else {
            handleDrawAction(drawStartSq, id, null);
        } return;
    }

    if (role === 'COACH' && (Object.keys(highlights).length > 0 || arrows.length > 0)) updateDoc(doc(db, "classes", classroomId), { boardHighlights: {}, boardArrows: [] });
    
    const targetPiece = game.get(id);
    const sourcePiece = selectedSquare ? game.get(selectedSquare) : null;
    const isSelectingOwnPiece = targetPiece && sourcePiece && targetPiece.color === sourcePiece.color && !freeMovement;

    if (selectedSquare && id !== selectedSquare && !isSelectingOwnPiece && (legalMoves.includes(id) || allowIllegal || freeMovement)) { 
        attemptMove(selectedSquare, id); 
    } 
    else {
        if (targetPiece) { 
            if (strictTurns && role !== 'COACH' && targetPiece.color !== game.turn() && !allowIllegal && !freeMovement) { setSelectedSquare(null); setLegalMoves([]); return; }
            setSelectedSquare(id); 

            const validTargets = Object.keys(boardItems);
            let tempForMoves = safeLoadFen(currentFen);
            if (!strictTurns || allowIllegal || freeMovement) {
                const tokens = tempForMoves.fen().split(' '); tokens[1] = targetPiece.color; 
                tempForMoves = safeLoadFen(tokens.join(' '));
            }
            
            let moves = [];
            if (freeMovement) {
                cols.forEach(c => rows.forEach(r => moves.push(`${c}${r}`)));
            } else {
                moves = tempForMoves.moves({ square: id, verbose: true }).map(m => m.to);
                if (targetPiece.type === 'p') {
                    const f = cols.indexOf(id[0]); const r = parseInt(id[1]); const dir = targetPiece.color === 'w' ? 1 : -1;
                    [-1, 1].forEach(df => { const diagSq = `${cols[f+df]}${r+dir}`; if (validTargets.includes(diagSq) && !moves.includes(diagSq)) moves.push(diagSq); });
                }
                if (targetPiece.type === 'k') {
                    if (targetPiece.color === 'w' && id === 'e1') {
                        if (!tempForMoves.get('f1') && !tempForMoves.get('g1') && tempForMoves.get('h1')?.type === 'r' && tempForMoves.get('h1')?.color === 'w' && !moves.includes('g1')) moves.push('g1');
                        if (!tempForMoves.get('d1') && !tempForMoves.get('c1') && !tempForMoves.get('b1') && tempForMoves.get('a1')?.type === 'r' && tempForMoves.get('a1')?.color === 'w' && !moves.includes('c1')) moves.push('c1');
                    }
                    if (targetPiece.color === 'b' && id === 'e8') {
                        if (!tempForMoves.get('f8') && !tempForMoves.get('g8') && tempForMoves.get('h8')?.type === 'r' && tempForMoves.get('h8')?.color === 'b' && !moves.includes('g8')) moves.push('g8');
                        if (!tempForMoves.get('d8') && !tempForMoves.get('c8') && !tempForMoves.get('b8') && tempForMoves.get('a8')?.type === 'r' && tempForMoves.get('a8')?.color === 'b' && !moves.includes('c8')) moves.push('c8');
                    }
                }
                if (moves.length === 0) moves = getPseudoLegalMoves(tempForMoves, id, validTargets); 
            }
            setLegalMoves(moves); 
        } else { setSelectedSquare(null); setLegalMoves([]); }
    }
  };

  // 🔥 FIXED PUZZLE LOGIC
  const openQuizModal = () => { setQuizTempGame(safeLoadFen(currentFen)); setQuizAns(''); setQuizSelectedSquare(null); setQuizLegalMoves([]); setShowQuizModal(true); };
  const resetQuizCreator = () => { setQuizTempGame(safeLoadFen(currentFen)); setQuizAns(''); setQuizSelectedSquare(null); setQuizLegalMoves([]); };
  
  const attemptQuizMove = (from, to) => { 
      try { 
          let temp = safeLoadFen(quizTempGame.fen()); 
          let move = temp.move({ from, to, promotion: 'q' }); 
          
          if (!move) {
              const piece = temp.get(from);
              if (piece) {
                  temp.remove(from); temp.put(piece, to);
                  const tokens = temp.fen().split(' '); tokens[1] = piece.color === 'w' ? 'b' : 'w';
                  temp = safeLoadFen(tokens.join(' '));
                  move = { san: `${piece.type.toUpperCase()==='P'?'':piece.type.toUpperCase()}@${to}` };
              }
          }

          if (move) { 
              setQuizTempGame(temp); playSound('move'); 
              const currentAns = quizAns ? quizAns.split(',').map(s=>s.trim()).filter(Boolean) : []; 
              const newAnsList = [...currentAns, move.san];
              setQuizAns(newAnsList.join(', '));
          } 
      } catch (e) {} 
      setQuizSelectedSquare(null); setQuizLegalMoves([]); 
  };
  
  const handleQuizBoardClick = (id) => { 
      if (quizLegalMoves.includes(id)) { 
          if (quizSelectedSquare) attemptQuizMove(quizSelectedSquare, id); 
      } else { 
          const piece = quizTempGame.get(id); 
          if (piece) { 
              setQuizSelectedSquare(id); 
              setQuizLegalMoves(getPseudoLegalMoves(quizTempGame, id)); 
          } else { 
              setQuizSelectedSquare(null); setQuizLegalMoves([]); 
          } 
      } 
  };

  const launchQuiz = async (e) => { e.preventDefault(); if (!quizAns) return toast.error("Please provide an answer!"); const answerArray = quizAns.split(',').map(s => s.trim()); await updateDoc(doc(db, "classes", classroomId), { classMode: 'QUIZ', studentFens: {}, quizData: { question: quizQues, answers: answerArray, endTime: Date.now() + (quizTime * 1000), points: parseInt(quizPts), active: true, solvedBy: [] } }); setShowQuizModal(false); toast.success("Puzzle Launched to Students! 🚀"); };
  const endQuizEarly = async () => { 
      const loadingId = toast.loading("Ending puzzle and updating scores...");
      try {
          const studentScores = classData?.studentScores || {};
          for (const emailKey of Object.keys(studentScores)) {
              const actualEmail = emailKey.replace(/_/g, '.');
              const pts = studentScores[emailKey] || 0;
              if (pts > 0) {
                  await updateDoc(doc(db, "users", actualEmail), {
                      totalXp: increment(pts)
                  }).catch(e => console.error("Failed to update XP", e));
              }
          }
          await updateDoc(doc(db, "classes", classroomId), { 
              'quizData.active': false, 
              classMode: 'PRACTICE',
              studentScores: {},
              quizAttempts: {}
          }); 
          toast.success("Quiz Ended manually & XP awarded! ✅", { id: loadingId });
      } catch (e) {
          toast.error("Failed to end quiz.", { id: loadingId });
      }
  };

  const toggleSquareCoords = async () => {
    const newVal = !showSquareCoords;
    setShowSquareCoords(newVal);
    if (classroomId) {
      await updateDoc(doc(db, "classes", classroomId), { showSquareCoords: newVal }).catch(e => console.error(e));
    }
  };

  const toggleFreeMovement = async () => {
    const newVal = !freeMovement;
    setFreeMovement(newVal);
    if (classroomId) {
      await updateDoc(doc(db, "classes", classroomId), { freeMovement: newVal }).catch(e => console.error(e));
    }
    toast(newVal ? "🖐️ Free Movement: ON" : "🖐️ Free Movement: OFF");
  };

  const openSetupModal = () => {
    const temp = safeLoadFen(currentFen);
    const b = {};
    temp.board().forEach((row, r) => row.forEach((sq, c) => {
      if (sq) b[`${String.fromCharCode(97+c)}${8-r}`] = sq.color + sq.type.toUpperCase();
    }));
    setSetupBoard(b);
    setSetupTurn(temp.turn());
    const tokens = currentFen.split(' ');
    const castlingStr = tokens.length > 2 ? tokens[2] : 'KQkq';
    setSetupCastling({
      K: castlingStr.includes('K'),
      Q: castlingStr.includes('Q'),
      k: castlingStr.includes('k'),
      q: castlingStr.includes('q')
    });
    setSetupTool(null);
    setShowSetup(true);
  };
  const getSetupFen = () => {
    let fen = '';
    for (let r = 8; r >= 1; r--) {
      let empty = 0;
      for (let c = 0; c < 8; c++) {
        const p = setupBoard[`${String.fromCharCode(97 + c)}${r}`];
        if (p) {
          if (empty > 0) { fen += empty; empty = 0; }
          fen += p[0] === 'w' ? p[1].toUpperCase() : p[1].toLowerCase();
        } else empty++;
      }
      if (empty > 0) fen += empty;
      if (r > 1) fen += '/';
    }
    let castlingStr = '';
    if (setupCastling.K) castlingStr += 'K';
    if (setupCastling.Q) castlingStr += 'Q';
    if (setupCastling.k) castlingStr += 'k';
    if (setupCastling.q) castlingStr += 'q';
    if (!castlingStr) castlingStr = '-';
    return `${fen} ${setupTurn} ${castlingStr} - 0 1`;
  };
  const confirmSetup = () => { try { const temp = safeLoadFen(getSetupFen()); setUndoStack([]); setRedoStack([]); syncBoard(temp, true, false, []); setShowSetup(false); } catch (e) { toast.error("Invalid Position!"); } };

  const openFenModal = () => { setFenInput(''); setShowFenModal(true); }; const confirmFen = (e) => { e.preventDefault(); try { syncBoard(safeLoadFen(fenInput), true, false, []); setShowFenModal(false); toast.success("Position Loaded!"); } catch(err) { toast.error("Invalid FEN!"); } };
  const openPgnModal = () => { setPgnInput(''); setShowPgnModal(true); }; const confirmPgn = (e) => { e.preventDefault(); try { const t = new Chess(); t.loadPgn(pgnInput); setUndoStack([]); setRedoStack([]); syncBoard(t, true, false, t.history()); setShowPgnModal(false); toast.success("Game Loaded!"); } catch(err) { toast.error("Invalid PGN format!"); } };
  const playEngineMove = async () => { if (game.isGameOver()) return toast("Game over! 🏁"); try { const res = await fetch("https://chess-api.com/v1", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fen: currentFen, depth: 12 }) }); const data = await res.json(); const temp = safeLoadFen(currentFen); const move = temp.move(data.san); if (move) { playSound(move.captured ? 'capture' : 'move'); syncBoard(temp, true, false, [...moveHistory, move.san]); } } catch (err) { toast.error("Engine failed to connect."); } };
  
  const sendEmoji = async () => { const emojis = ['🔥', '🤯', '👏', '🎯', '💀', '🧠', '⭐']; await updateDoc(doc(db, "classes", classroomId), { emojiEvent: { emoji: emojis[Math.floor(Math.random() * emojis.length)], time: Date.now() } }); };
  const toggleHidePieces = async () => { const newVal = !hidePieces; setHidePieces(newVal); await updateDoc(doc(db, "classes", classroomId), { hidePieces: newVal }); };
  const toggleMoveAssist = async () => { const newVal = !moveAssist; setMoveAssist(newVal); await updateDoc(doc(db, "classes", classroomId), { moveAssist: newVal }); toast(newVal ? "✨ Dots: ON" : "✨ Dots: OFF"); };
  const toggleIllegalMoves = async () => { const newVal = !allowIllegal; setAllowIllegal(newVal); await updateDoc(doc(db, "classes", classroomId), { allowIllegal: newVal }); };
  
  const handleClearBoard = () => { const t = new Chess(); t.clear(); syncBoard(t, true, false, []); setQuickTool(null); setIsDrawMode(false); setBoardItems({}); updateDoc(doc(db, "classes", classroomId), { boardItems: {} }); toast("Board Cleared! Use Quick Palette to place pieces.", {icon: '🧹'}); };
  const resetBoard = () => { 
    const freshTree = createEmptyTree();
    setGameTree(freshTree);
    syncBoard(new Chess(), true, false, []); 
    setBoardItems({}); 
    if (classroomId) {
      updateDoc(doc(db, "classes", classroomId), { boardItems: {}, gameTree: freshTree, variations: [], moveComments: {} }).catch(e => console.error(e)); 
    }
    toast.success("Fresh game started! Board & tree reset. ♟️🌱");
  };
  
  const loadPreset = (presetName) => {
    const presets = {
      standard: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      empty: '8/8/8/8/8/8/8/8 w - - 0 1',
      kp: '4k3/8/8/8/8/4P3/8/4K3 w - - 0 1',
      kr: '4k3/8/8/8/8/8/4R3/4K3 w - - 0 1',
      kq: '4k3/8/8/8/8/8/4Q3/4K3 w - - 0 1',
      kbb: '4k3/8/8/8/8/8/4BB2/4K3 w - - 0 1',
    };
    const fen = presets[presetName];
    if (fen) {
      try {
        syncBoard(safeLoadFen(fen), true, false, []);
        setBoardItems({});
        if (classroomId) {
          updateDoc(doc(db, "classes", classroomId), { boardItems: {} }).catch(e => console.error(e));
        }
        toast.success(`Position Loaded: ${presetName.toUpperCase()}`);
      } catch(e) {
        toast.error("Error loading preset");
      }
    }
  };

  const copyPosition = () => { navigator.clipboard.writeText(currentFen); toast.success("FEN Copied!"); };
  
  const toggleStrictTurns = async () => { const newVal = !strictTurns; setStrictTurns(newVal); await updateDoc(doc(db, "classes", classroomId), { strictTurns: newVal }); toast(newVal ? "🛡️ Standard Turn Rules ON" : "♾️ Continuous Moves Allowed!"); };
  const toggleRecording = async () => { if (isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); } else { try { const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true }); mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'video/webm' }); mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) recordedChunks.current.push(e.data); }; mediaRecorderRef.current.onstop = () => { const blob = new Blob(recordedChunks.current, { type: 'video/webm' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `HayaaChess-Class-${new Date().toLocaleTimeString().replace(/:/g,'-')}.webm`; a.click(); window.URL.revokeObjectURL(url); recordedChunks.current = []; toast.success("Recording saved to Downloads!"); }; mediaRecorderRef.current.start(); setIsRecording(true); toast.success("Recording Started 🔴"); } catch (err) { toast.error("Screen recording permission denied."); } } };

  const clearHighlightsAndArrows = async () => {
    setHighlights({});
    setArrows([]);
    if (classroomId) {
      await updateDoc(doc(db, "classes", classroomId), { boardHighlights: {}, boardArrows: [] }).catch(e => console.error(e));
    }
    toast.success("Drawings Cleared!");
  };

  const baseRows = [8, 7, 6, 5, 4, 3, 2, 1]; const baseCols = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const rows = isFlipped ? [...baseRows].reverse() : baseRows; const cols = isFlipped ? [...baseCols].reverse() : baseCols;
  const renderBoard = {};
  game.board().forEach((row, rIdx) => { row.forEach((sq, cIdx) => { if (sq) renderBoard[`${String.fromCharCode(97 + cIdx)}${8 - rIdx}`] = sq.color + sq.type.toUpperCase(); }); });

  const canSeeDots = role === 'COACH' ? moveAssist : studentDots;

  const hasSidebar = role === 'COACH' && !isIndividualMode;
  const sidebarActive = hasSidebar && showPalette;
  return (
    <div className="chess-board-container" style={{ 
        position: 'relative', 
        width: '100%', 
        maxWidth: sidebarActive ? `calc(min(100%, ${boardWidth}px) + 125px)` : `min(100%, ${boardWidth}px)`, 
        margin: '0 auto', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '6px',
        paddingLeft: sidebarActive ? '125px' : '0',
        boxSizing: 'border-box',
        transition: 'padding-left 0.25s ease, max-width 0.25s ease'
    }}>
        {sidebarActive && (
            <div style={{
                position: 'absolute',
                left: '5px',
                top: '25px',
                width: '112px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                background: '#e0e5ec',
                padding: '12px 10px',
                borderRadius: '16px',
                boxShadow: 'var(--panel-shadow)',
                border: '1px solid rgba(255,255,255,0.6)',
                zIndex: 10,
                boxSizing: 'border-box'
            }} className="hide-on-mobile">
                 <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pieces</div>
                 
                 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px' }}>
                     {['wK','wQ','wR','wB','wN','wP','bK','bQ','bR','bB','bN','bP'].map(p => (
                         <img 
                             key={p} 
                             src={PIECE_IMAGES[p]} 
                             alt={p} 
                             draggable={false}
                             onPointerDown={(e) => { 
                                 e.preventDefault(); 
                                 setQuickTool(p); 
                                 setIsDrawMode(false); 
                                 setDraggingPiece({ type: 'palette', item: p, x: e.clientX, y: e.clientY }); 
                             }}
                             style={{ 
                                 width: '36px', 
                                 height: '36px', 
                                 cursor: 'pointer', 
                                 background: quickTool === p ? 'rgba(59, 130, 246, 0.2)' : 'transparent', 
                                 borderRadius: '6px', 
                                 border: quickTool === p ? '2px solid #3b82f6' : 'none', 
                                 padding: '2px', 
                                 touchAction: 'none' 
                             }}
                         />
                     ))}
                 </div>
                 
                 <div style={{ height: '1px', background: 'rgba(163, 177, 198, 0.4)', margin: '2px 0' }} />
                 
                 <button 
                     onPointerDown={(e) => { 
                         e.preventDefault(); 
                         setQuickTool('eraser'); 
                         setIsDrawMode(false); 
                         setDraggingPiece({ type: 'palette', item: 'eraser', x: e.clientX, y: e.clientY }); 
                     }} 
                     style={{ 
                         width: '100%', 
                         padding: '6px', 
                         cursor: 'pointer', 
                         background: quickTool === 'eraser' ? '#ef4444' : 'var(--bg-primary)', 
                         color: quickTool === 'eraser' ? 'white' : 'var(--text-primary)', 
                         borderRadius: '6px', 
                         border: '1px solid rgba(255,255,255,0.6)', 
                         fontSize: '11px', 
                         fontWeight: 'bold',
                         boxShadow: 'var(--neu-shadow)' 
                     }}
                     title="Piece Eraser"
                 >
                     🧽 Eraser
                 </button>

                 {quickTool && (
                     <button 
                         onClick={() => { 
                             setQuickTool(null); 
                             toast.success("Build Mode Completed!"); 
                         }} 
                         style={{ 
                             width: '100%', 
                             padding: '8px', 
                             background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                             color: 'white', 
                             border: 'none', 
                             borderRadius: '8px', 
                             fontWeight: 'bold', 
                             cursor: 'pointer', 
                             fontSize: '11px',
                             boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)'
                         }}
                     >
                         ✅ Done
                     </button>
                 )}

                 <div style={{ height: '1.5px', background: 'rgba(163, 177, 198, 0.4)', margin: '2px 0' }} />
                 <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Controls</div>

                 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px' }}>
                     {/* Reset Board */}
                     <button
                         onClick={resetBoard}
                         style={{
                             padding: '6px 0',
                             borderRadius: '6px',
                             background: '#e0e5ec',
                             border: '1px solid rgba(255,255,255,0.6)',
                             boxShadow: 'var(--neu-shadow)',
                             cursor: 'pointer',
                             fontSize: '12px',
                             display: 'flex',
                             justifyContent: 'center',
                             alignItems: 'center'
                         }}
                         title="Reset starting position"
                     >
                         🔄
                     </button>
                     
                     {/* Clear pieces */}
                     <button
                         onClick={handleClearBoard}
                         style={{
                             padding: '6px 0',
                             borderRadius: '6px',
                             background: '#e0e5ec',
                             border: '1px solid rgba(255,255,255,0.6)',
                             boxShadow: 'var(--neu-shadow)',
                             cursor: 'pointer',
                             fontSize: '12px',
                             display: 'flex',
                             justifyContent: 'center',
                             alignItems: 'center'
                         }}
                         title="Clear all board pieces"
                     >
                         🗑️
                     </button>
                     
                     {/* Freestyle play toggle */}
                     <button
                         onClick={toggleFreeMovement}
                         style={{
                             padding: '6px 0',
                             borderRadius: '6px',
                             background: freeMovement ? '#3b82f6' : '#e0e5ec',
                             color: freeMovement ? 'white' : 'var(--text-primary)',
                             border: '1px solid rgba(255,255,255,0.6)',
                             boxShadow: freeMovement ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                             cursor: 'pointer',
                             fontSize: '12px',
                             display: 'flex',
                             justifyContent: 'center',
                             alignItems: 'center',
                             transition: 'all 0.15s'
                         }}
                         title="Freestyle / Manual movement"
                     >
                         🖐️
                     </button>
                     
                     {/* Setup Position */}
                     <button
                         onClick={openSetupModal}
                         style={{
                             padding: '6px 0',
                             borderRadius: '6px',
                             background: showSetup ? '#3b82f6' : '#e0e5ec',
                             color: showSetup ? 'white' : 'var(--text-primary)',
                             border: '1px solid rgba(255,255,255,0.6)',
                             boxShadow: showSetup ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                             cursor: 'pointer',
                             fontSize: '12px',
                             display: 'flex',
                             justifyContent: 'center',
                             alignItems: 'center',
                             transition: 'all 0.15s'
                         }}
                         title="Setup position layout"
                     >
                         🛠️
                     </button>
                 </div>
            </div>
        )}
        {individualBoardActive && role === 'STUDENT' && (
            <div style={{
                background: 'linear-gradient(95deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 'bold',
                textAlign: 'center',
                boxShadow: '0 4px 10px rgba(16, 185, 129, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
            }}>
                <span>🟢</span> Individual Board Active (Private to Coach)
            </div>
        )}
        <style>{`
            @media (max-width: 768px) { .hide-on-mobile { display: none !important; } .chess-board-container { max-width: 100% !important; padding: 0 !important; } }
            .edge-resize-handle { transition: background 0.2s; background: transparent; }
            .edge-resize-handle:hover { background: rgba(59, 130, 246, 0.45) !important; }
            
            .neu-board-container {
                display: flex;
                align-items: center;
                gap: 6px;
                background: var(--bg-primary);
                padding: 6px 12px;
                border-radius: 30px;
                box-shadow: var(--neu-shadow);
                border: 1.5px solid rgba(255, 255, 255, 0.8);
                width: fit-content;
                margin: 5px auto;
                box-sizing: border-box;
            }
            .neu-btn-circle {
                background: var(--bg-primary);
                color: var(--text-primary);
                border: none;
                width: 28px;
                height: 28px;
                border-radius: 50%;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 13px;
                font-weight: bold;
                box-shadow: var(--neu-shadow);
                transition: all 0.15s ease;
                outline: none;
                box-sizing: border-box;
            }
            .neu-btn-circle:hover:not(:disabled) {
                box-shadow: var(--neu-shadow-hover);
                transform: translateY(-1px);
            }
            .neu-btn-circle:active:not(:disabled), .neu-btn-circle.active {
                box-shadow: var(--neu-shadow-inset);
                transform: translateY(0);
            }
            .neu-btn-circle:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
            .neu-btn-square {
                background: var(--bg-primary);
                color: var(--text-primary);
                border: 1px solid rgba(255, 255, 255, 0.8);
                width: 32px;
                height: 32px;
                border-radius: 8px;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 15px;
                box-shadow: var(--neu-shadow);
                transition: all 0.15s ease;
                outline: none;
                box-sizing: border-box;
            }
            .neu-btn-square:hover:not(:disabled) {
                box-shadow: var(--neu-shadow-hover);
                transform: translateY(-1px);
            }
            .neu-btn-square:active:not(:disabled), .neu-btn-square.active {
                box-shadow: var(--neu-shadow-inset);
                transform: translateY(0);
                border: 1.5px solid #3b82f6;
            }
            .neu-select {
                padding: 4px 8px;
                background: var(--bg-primary);
                color: var(--text-primary);
                border: none;
                border-radius: 15px;
                box-shadow: var(--neu-shadow-inset);
                cursor: pointer;
                font-weight: bold;
                font-size: 11px;
                height: 28px;
                outline: none;
                font-family: inherit;
                transition: all 0.15s ease;
            }
            .neu-select:hover {
                background: var(--btn-hover);
            }
            .neu-panel-inset {
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
                background: var(--bg-primary);
                padding: 8px 12px;
                border-radius: 30px;
                box-shadow: var(--neu-shadow-inset);
                border: 1.5px solid rgba(255, 255, 255, 0.8);
                width: fit-content;
                margin: 5px auto;
                box-sizing: border-box;
            }
            .neu-palette-panel {
                display: flex; 
                justify-content: center; 
                gap: 6px; 
                flex-wrap: wrap; 
                background: var(--bg-primary); 
                padding: 10px; 
                border-radius: 16px; 
                align-items: center; 
                box-shadow: var(--neu-shadow-inset);
                border: 1.5px solid rgba(255, 255, 255, 0.8);
                margin-top: 5px;
                box-sizing: border-box;
                width: 100%;
            }
        `}</style>

        {(draggingPiece || drawStartSq) && createPortal(
            <div 
                style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99998, touchAction: 'none' }}
                onPointerMove={(e) => { 
                    if (draggingPiece) { 
                        setDraggingPiece(prev => ({...prev, x: e.clientX, y: e.clientY})); 
                        if (draggingPiece.item === 'eraser') {
                            e.currentTarget.style.pointerEvents = 'none';
                            const targetEl = document.elementFromPoint(e.clientX, e.clientY);
                            const targetSq = targetEl?.closest('[data-square]')?.getAttribute('data-square');
                            e.currentTarget.style.pointerEvents = 'auto';
                            if (targetSq) {
                                let changed = false;
                                if (boardItems[targetSq]) {
                                    const newItems = { ...boardItems };
                                    delete newItems[targetSq];
                                    setBoardItems(newItems);
                                    updateDoc(doc(db, "classes", classroomId), { boardItems: newItems });
                                    changed = true;
                                }
                                const temp = safeLoadFen(currentFen);
                                if (temp.get(targetSq)) {
                                    temp.remove(targetSq);
                                    syncBoard(temp, false);
                                    changed = true;
                                }
                            }
                        }
                    } 
                }}
                onPointerUp={(e) => {
                    e.currentTarget.style.pointerEvents = 'none'; 
                    const targetEl = document.elementFromPoint(e.clientX, e.clientY);
                    const targetSq = targetEl?.closest('[data-square]')?.getAttribute('data-square');
                    e.currentTarget.style.pointerEvents = 'auto';
                    
                    if (draggingPiece) {
                        if (targetSq) {
                            if (draggingPiece.type === 'palette') {
                                if (CUTE_ITEMS.includes(draggingPiece.item)) {
                                    const newItems = { ...boardItems, [targetSq]: draggingPiece.item }; setBoardItems(newItems); updateDoc(doc(db, "classes", classroomId), { boardItems: newItems });
                                } else {
                                    const temp = safeLoadFen(currentFen);
                                    if (draggingPiece.item === 'eraser') {
                                        if (boardItems[targetSq]) { const newItems = { ...boardItems }; delete newItems[targetSq]; setBoardItems(newItems); updateDoc(doc(db, "classes", classroomId), { boardItems: newItems }); }
                                        temp.remove(targetSq);
                                    } else { temp.put({ type: draggingPiece.item[1].toLowerCase(), color: draggingPiece.item[0] }, targetSq); }
                                    syncBoard(temp, false);
                                }
                            } else if (draggingPiece.type === 'boardItem' && targetSq !== draggingPiece.id) {
                                const newItems = { ...boardItems }; delete newItems[draggingPiece.id]; newItems[targetSq] = draggingPiece.item; setBoardItems(newItems); updateDoc(doc(db, "classes", classroomId), { boardItems: newItems });
                            } else if (draggingPiece.type === 'board' && targetSq !== draggingPiece.id) { attemptMove(draggingPiece.id, targetSq); }
                        } else if (dropToDelete) {
                            // Dropped outside board — only delete when dropToDelete mode is ON
                            if (draggingPiece.type === 'board') {
                                const temp = safeLoadFen(currentFen);
                                temp.remove(draggingPiece.id);
                                syncBoard(temp, false);
                            } else if (draggingPiece.type === 'boardItem') {
                                const newItems = { ...boardItems };
                                delete newItems[draggingPiece.id];
                                setBoardItems(newItems);
                                updateDoc(doc(db, "classes", classroomId), { boardItems: newItems });
                            }
                        }
                    }
                    if (drawStartSq && targetSq) {
                        handleDrawAction(drawStartSq, targetSq, e);
                    }
                    setDraggingPiece(null); setDrawStartSq(null); setQuickTool(null);
                }}
            />,
            document.body
        )}

        {draggingPiece && createPortal(
            <div style={{ position: 'fixed', top: draggingPiece.y - 25, left: draggingPiece.x - 25, width: '50px', height: '50px', pointerEvents: 'none', zIndex: 99999, filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.5))', transform: 'scale(1.2)' }}>
                {draggingPiece.item === 'eraser' ? <div style={{width:'100%', height:'100%', background:'#ef4444', borderRadius:'8px', display:'flex', justifyContent:'center', alignItems:'center', fontSize:'24px', border:'2px solid white'}}>🧹</div> : 
                 CUTE_ITEMS.includes(draggingPiece.item) ? <div style={{fontSize: '40px', display:'flex', justifyContent:'center', alignItems:'center', width:'100%', height:'100%'}}>{draggingPiece.item === '⭐' ? <img src={STAR_ICON} style={{width:'80%',height:'80%'}} alt="star" /> : draggingPiece.item}</div> :
                 <img src={(PIECE_SETS[pieceTheme]?.[draggingPiece.item] || PIECE_IMAGES[draggingPiece.item])} alt="drag" style={{width:'100%', height:'100%'}} />}
            </div>,
            document.body
        )}

        {/* REPAIRED SETUP MODAL USING PORTAL TO PREVENT Z-INDEX OVERLAYS */}
        {showSetup && createPortal(
            <div style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                background: 'rgba(15, 23, 42, 0.45)', 
                backdropFilter: 'blur(8px)', 
                WebkitBackdropFilter: 'blur(8px)', 
                zIndex: 99999, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                padding: '10px', 
                boxSizing: 'border-box' 
            }}>
                <div style={{ 
                    background: '#e0e5ec', 
                    padding: '24px', 
                    borderRadius: '24px', 
                    width: '100%', 
                    maxWidth: '380px', 
                    maxHeight: '95vh', 
                    overflowY: 'auto', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '15px', 
                    boxShadow: '8px 8px 20px rgba(163, 177, 198, 0.2), -8px -8px 20px rgba(255, 255, 255, 0.55)', 
                    border: '1px solid rgba(255, 255, 255, 0.45)',
                    boxSizing: 'border-box'
                }}>
                    <h3 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '18px', fontWeight: '800' }}>
                        ⚙️ Setup Board
                        <span onClick={() => setShowSetup(false)} style={{ cursor: 'pointer', color: '#ef4444', fontSize: '20px' }}>✖</span>
                    </h3>
                    
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap', background: '#e0e5ec', padding: '10px', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255, 255, 255, 0.45)' }}>
                        {['wK','wQ','wR','wB','wN','wP','bK','bQ','bR','bB','bN','bP'].map(p => (
                            <img 
                                key={p} 
                                src={(PIECE_SETS[pieceTheme]?.[p] || PIECE_IMAGES[p])} 
                                alt={p} 
                                onClick={()=>setSetupTool(p)} 
                                draggable={false}
                                onPointerDown={(e) => handleSetupDragStart(e, p, '')}
                                style={{
                                    width:'30px', 
                                    height:'30px', 
                                    cursor:'grab', 
                                    background: setupTool===p ? 'rgba(59, 130, 246, 0.2)':'transparent', 
                                    borderRadius:'6px', 
                                    padding: '2px',
                                    boxShadow: setupTool===p ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                    border: setupTool===p ? '2px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.4)',
                                    transition: 'all 0.15s',
                                    touchAction: 'none'
                                }}
                            />
                        ))}
                        <div 
                            onClick={()=>setSetupTool('eraser')} 
                            data-setup-trash="true"
                            style={{
                                width:'30px', 
                                height:'30px', 
                                cursor:'pointer', 
                                background: setupTool==='eraser' ? '#ef4444':'var(--btn-bg)', 
                                color: setupTool==='eraser' ? 'white' : 'var(--text-primary)', 
                                borderRadius:'6px', 
                                display:'flex', 
                                justifyContent:'center', 
                                alignItems:'center', 
                                boxShadow: setupTool==='eraser' ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                border: '1px solid rgba(255, 255, 255, 0.4)',
                                transition: 'all 0.15s'
                            }}
                            title="Piece Eraser"
                        >
                            🧽
                        </div>
                    </div>

                    <div style={{ 
                        width: '100%', 
                        maxWidth: '280px', 
                        margin: '0 auto', 
                        aspectRatio: '1/1', 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(8, 1fr)', 
                        gridTemplateRows: 'repeat(8, 1fr)', 
                        border: '6px solid var(--bg-primary)', 
                        borderRadius: '16px', 
                        boxShadow: 'var(--neu-shadow-inset), 0 4px 10px rgba(0,0,0,0.06)',
                        overflow: 'hidden' 
                    }}>
                        {baseRows.map((row, rIdx) => baseCols.map((col, cIdx) => { 
                            const sq = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1; 
                            return (
                                <div 
                                    key={sq} 
                                    onClick={() => {
                                        if (isSetupDraggingRef.current) return;
                                        const nb = {...setupBoard};
                                        if(setupTool === 'eraser') delete nb[sq];
                                        else if(setupTool) nb[sq] = setupTool;
                                        setSetupBoard(nb);
                                    }} 
                                    data-setup-square={sq}
                                    style={{
                                        background: isDark ? (BOARD_THEMES[boardTheme]?.dark || '#8ca2ad') : (BOARD_THEMES[boardTheme]?.light || '#dee3e6'),
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: setupTool ? 'crosshair' : 'grab',
                                        position: 'relative'
                                    }}
                                >
                                    {setupBoard[sq] && (
                                        <img 
                                            src={(PIECE_SETS[pieceTheme]?.[setupBoard[sq]] || PIECE_IMAGES[setupBoard[sq]])} 
                                            alt={setupBoard[sq]} 
                                            draggable={false}
                                            onPointerDown={(e) => handleSetupDragStart(e, setupBoard[sq], sq)}
                                            style={{
                                                width: '85%', 
                                                height: '85%', 
                                                cursor: 'grab',
                                                touchAction: 'none'
                                            }} 
                                        />
                                    )}
                                </div>
                            ) 
                        }))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                        <select 
                            value={setupTurn} 
                            onChange={e=>setSetupTurn(e.target.value)} 
                            style={{
                                padding: '8px 10px', 
                                background: 'var(--bg-primary)', 
                                color: 'var(--text-primary)', 
                                border: '1px solid rgba(255, 255, 255, 0.45)', 
                                borderRadius: '8px', 
                                cursor: 'pointer',
                                boxShadow: 'var(--neu-shadow)',
                                fontWeight: '600',
                                outline: 'none',
                                flex: 1
                            }}
                        >
                            <option value="w">White to play</option>
                            <option value="b">Black to play</option>
                        </select>
                        
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <button onClick={()=>{ setSetupBoard({}); setSetupCastling({ K: false, Q: false, k: false, q: false }); }} style={{ padding: '8px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)' }}>Empty</button>
                            <button 
                                onClick={()=>{
                                    const temp = new Chess();
                                    const b = {};
                                    temp.board().forEach((row, r) => row.forEach((sq, c) => {
                                        if(sq) b[`${String.fromCharCode(97+c)}${8-r}`] = sq.color + sq.type.toUpperCase();
                                    }));
                                    setSetupBoard(b);
                                    setSetupTurn('w');
                                    setSetupCastling({ K: true, Q: true, k: true, q: true });
                                }} 
                                style={{
                                    padding: '8px 12px', 
                                    background: 'var(--btn-bg)', 
                                    color: 'var(--text-primary)', 
                                    border: '1px solid rgba(255,255,255,0.7)', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer', 
                                    fontWeight: 'bold',
                                    boxShadow: 'var(--neu-shadow)'
                                }}
                            >
                                Start Pos
                            </button>
                        </div>
                    </div>

                    {/* 🏰 CASTLING RIGHTS CONTROLS */}
                    <div style={{ 
                        background: '#e0e5ec', 
                        padding: '12px 16px', 
                        borderRadius: '12px', 
                        boxShadow: 'var(--neu-shadow-inset)', 
                        border: '1px solid rgba(255, 255, 255, 0.55)', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '8px' 
                    }}>
                        <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)' }}>🏰 Castling Rights Allowed:</span>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', fontWeight: '600' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                <input type="checkbox" checked={setupCastling.K} onChange={e => setSetupCastling({...setupCastling, K: e.target.checked})} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> White O-O
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                <input type="checkbox" checked={setupCastling.Q} onChange={e => setSetupCastling({...setupCastling, Q: e.target.checked})} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> White O-O-O
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                <input type="checkbox" checked={setupCastling.k} onChange={e => setSetupCastling({...setupCastling, k: e.target.checked})} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> Black O-O
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                <input type="checkbox" checked={setupCastling.q} onChange={e => setSetupCastling({...setupCastling, q: e.target.checked})} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> Black O-O-O
                            </label>
                        </div>
                    </div>

                    <button 
                        onClick={confirmSetup} 
                        style={{
                            width: '100%', 
                            padding: '12px', 
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                            color: 'black', 
                            fontWeight: '800', 
                            border: 'none', 
                            borderRadius: '12px', 
                            cursor: 'pointer', 
                            fontSize: '16px',
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3), inset 2px 2px 5px rgba(255,255,255,0.2)',
                            transition: 'all 0.25s'
                        }}
                    >
                        ✅ Load to Board
                    </button>
                </div>
            </div>,
            document.body
        )}

        {/* OTHER MODALS */}
        {showFenModal && ( <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px', boxSizing: 'border-box' }}><div style={{ background: '#1e293b', padding: '25px', borderRadius: '12px', width: '100%', maxWidth: '400px', border: '1px solid #7df9ff', boxShadow: '0 0 20px rgba(125, 249, 255, 0.2)' }}><h3 style={{ margin: '0 0 15px 0', color: '#7df9ff', display: 'flex', justifyContent: 'space-between' }}>📥 Load Position (FEN)<span onClick={() => setShowFenModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span></h3><form onSubmit={confirmFen} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}><input type="text" value={fenInput} onChange={e=>setFenInput(e.target.value)} placeholder="Paste FEN string here..." required style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #334155', background: '#0f172a', color: 'white', boxSizing: 'border-box', fontFamily: 'monospace' }} /><button type="submit" style={{ width: '100%', padding: '12px', background: '#3b82f6', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Load Board</button></form></div></div> )}
        {showPgnModal && ( <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px', boxSizing: 'border-box' }}><div style={{ background: '#1e293b', padding: '25px', borderRadius: '12px', width: '100%', maxWidth: '450px', border: '1px solid #10b981', boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' }}><h3 style={{ margin: '0 0 15px 0', color: '#10b981', display: 'flex', justifyContent: 'space-between' }}>📜 Load Game (PGN)<span onClick={() => setShowPgnModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span></h3><form onSubmit={confirmPgn} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}><textarea value={pgnInput} onChange={e=>setPgnInput(e.target.value)} placeholder="Paste PGN text here...&#10;e.g. 1. e4 e5 2. Nf3 Nc6..." required style={{ width: '100%', height: '150px', padding: '12px', borderRadius: '6px', border: '1px solid #334155', background: '#0f172a', color: 'white', boxSizing: 'border-box', fontFamily: 'monospace', resize: 'none' }} /><button type="submit" style={{ width: '100%', padding: '12px', background: '#10b981', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>Load Game</button></form></div></div> )}
 
        {/* REPAIRED PUZZLE CREATOR MODAL */}
        {showQuizModal && ( 
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px', boxSizing: 'border-box' }}>
                <div style={{ background: '#1e293b', padding: '20px', borderRadius: '12px', width: '100%', maxWidth: '450px', maxHeight: '95vh', overflowY: 'auto', border: '1px solid #f59e0b', boxShadow: '0 0 20px rgba(245, 158, 11, 0.2)' }}>
                    <h3 style={{ margin: '0 0 15px 0', color: '#f59e0b', display: 'flex', justifyContent: 'space-between' }}>🎯 Interactive Puzzle Creator<span onClick={() => setShowQuizModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span></h3>
                    <p style={{ margin: '0 0 10px 0', color: '#94a3b8', fontSize: '13px' }}>👉 Play the correct move(s) on this board to auto-fill the answer:</p>
                    <div style={{ width: '100%', maxWidth: '280px', margin: '0 auto 15px auto', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', border: '2px solid #334155', borderRadius: '4px', overflow: 'hidden' }}>
                        {rows.map((row, rIdx) => cols.map((col, cIdx) => { 
                            const id = `${col}${row}`; const isDark = isFlipped ? (rIdx + cIdx) % 2 === 0 : (rIdx + cIdx) % 2 === 1; const pieceObj = quizTempGame.get(id); 
                            return ( 
                                <div key={id} onPointerDown={() => handleQuizBoardClick(id)} style={{ position: 'relative', background: quizSelectedSquare === id ? '#7df9ff' : (isDark ? (BOARD_THEMES[boardTheme]?.dark || '#8ca2ad') : (BOARD_THEMES[boardTheme]?.light || '#dee3e6')), display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                                    {quizLegalMoves.includes(id) && <div style={{width: '30%', height: '30%', background: 'rgba(0,0,0,0.2)', borderRadius: '50%', position: 'absolute', pointerEvents: 'none'}} />}
                                    {pieceObj && <img src={(PIECE_SETS[pieceTheme]?.[pieceObj.color + pieceObj.type.toUpperCase()] || PIECE_IMAGES[pieceObj.color + pieceObj.type.toUpperCase()])} alt="p" style={{ width: '85%', height: '85%', pointerEvents: 'none' }} />}
                                </div> 
                            ); 
                        }))}
                    </div>
                    <form onSubmit={launchQuiz} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div><label style={{ color: '#94a3b8', fontSize: '13px', display: 'block' }}>Question / Hint</label><input type="text" value={quizQues} onChange={e=>setQuizQues(e.target.value)} required style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #334155', background: '#0f172a', color: 'white', boxSizing: 'border-box' }} /></div>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <label style={{ fontSize: '13px', color: '#10b981', fontWeight: 'bold' }}>Correct Answers (Auto-filled sequence or type SAN)</label>
                                <button type="button" onClick={resetQuizCreator} style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '4px', fontSize: '11px', padding: '2px 6px', cursor: 'pointer', fontWeight: 'bold' }}>🔄 Reset Moves</button>
                            </div>
                            <input type="text" value={quizAns} onChange={e=>setQuizAns(e.target.value)} placeholder="Play sequence on board above or type e.g. Rxc6, bxc6, Qxc6+" required style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #10b981', background: '#0f172a', color: '#10b981', fontWeight: 'bold', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: 1 }}><label style={{ color: '#94a3b8', fontSize: '13px', display: 'block' }}>Timer (Sec)</label><input type="number" value={quizTime} onChange={e=>setQuizTime(e.target.value)} required min="10" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: 'none', background: '#0f172a', color: 'white', boxSizing: 'border-box' }} /></div>
                            <div style={{ flex: 1 }}><label style={{ color: '#94a3b8', fontSize: '13px', display: 'block' }}>XP Points</label><input type="number" value={quizPts} onChange={e=>setQuizPts(e.target.value)} required min="1" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: 'none', background: '#0f172a', color: 'white', boxSizing: 'border-box' }} /></div>
                        </div>
                        <button type="submit" style={{ width: '100%', padding: '12px', background: '#f59e0b', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', marginTop: '10px' }}>🚀 Launch Puzzle</button>
                    </form>
                </div>
            </div> 
        )}

        <div onContextMenu={e => e.preventDefault()} style={{ width: boardWidth, height: boardWidth, flexShrink: 0, position: 'relative', touchAction: 'none' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '4px', overflow: 'hidden' }}>
                {floatingEmoji && (<div style={{ position: 'absolute', top: '40%', left: '40%', fontSize: '100px', zIndex: 100, pointerEvents: 'none', animation: 'floatUp 2s ease-out forwards', filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.5))' }}>{floatingEmoji}</div>)}
                <div style={{ width: '100%', height: '100%', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)`, border: `2px solid #334155`, background: '#334155', boxSizing: 'border-box' }}>
                {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                    const id = `${col}${row}`; const isDark = isFlipped ? (rIdx + cIdx) % 2 === 0 : (rIdx + cIdx) % 2 === 1;
                    return (
                    <div key={id} data-square={id}
                        onPointerDown={(e) => { 
                            if (role === 'COACH' && (e.button === 2 || isDrawMode)) { setDrawStartSq(id); } 
                            else if (!draggingPiece && !quickTool) { handleClick(id); } 
                        }}
                        style={{ position: 'relative', background: selectedSquare === id || drawStartSq === id ? '#7df9ff' : (isDark ? (BOARD_THEMES[boardTheme]?.dark || '#8ca2ad') : (BOARD_THEMES[boardTheme]?.light || '#dee3e6')), display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {highlights[id] && (
                            <div style={{ 
                                position: 'absolute', 
                                width: '84%', 
                                height: '84%', 
                                border: `4.5px solid ${highlights[id]}`, 
                                borderRadius: '50%', 
                                pointerEvents: 'none', 
                                boxSizing: 'border-box',
                                zIndex: 1 
                            }} />
                        )}
                        
                        {legalMoves.includes(id) && canSeeDots && <div style={{width: '30%', height: '30%', background: 'rgba(0,0,0,0.2)', borderRadius: '50%', position: 'absolute', zIndex: 5, pointerEvents: 'none'}} />}
                        
                        {showCoords && cIdx === 0 && <span style={{ position: 'absolute', top: 2, left: 2, fontSize: '10px', fontWeight: 'bold', color: isDark ? (BOARD_THEMES[boardTheme]?.light || '#dee3e6') : (BOARD_THEMES[boardTheme]?.dark || '#8ca2ad'), pointerEvents: 'none' }}>{row}</span>}
                        {showCoords && rIdx === 7 && <span style={{ position: 'absolute', bottom: 2, right: 2, fontSize: '10px', fontWeight: 'bold', color: isDark ? (BOARD_THEMES[boardTheme]?.light || '#dee3e6') : (BOARD_THEMES[boardTheme]?.dark || '#8ca2ad'), pointerEvents: 'none' }}>{col}</span>}
                        {showSquareCoords && (
                            <span style={{ 
                                position: 'absolute', 
                                fontSize: '11px', 
                                fontWeight: 'bold', 
                                opacity: 0.4, 
                                color: isDark ? (BOARD_THEMES[boardTheme]?.light || '#dee3e6') : (BOARD_THEMES[boardTheme]?.dark || '#8ca2ad'), 
                                pointerEvents: 'none',
                                zIndex: 1
                            }}>
                                {id}
                            </span>
                        )}
                    </div>);
                }))}
                </div>

                {Object.entries(boardItems).map(([id, item]) => {
                    const cIdx = cols.indexOf(id[0]); const rIdx = rows.indexOf(parseInt(id[1]));
                    const isCoach = role === 'COACH' && !isDrawMode && !quickTool;
                    return (
                        <div key={`item-${id}`} 
                            data-square={id} 
                            onPointerDown={(e) => { 
                                if (isCoach && !selectedSquare) { 
                                    e.preventDefault(); setDraggingPiece({ type: 'boardItem', id, item, x: e.clientX, y: e.clientY }); 
                                } else if (!quickTool) {
                                    e.preventDefault(); handleClick(id); 
                                }
                            }}
                            style={{ position: 'absolute', width: '12.5%', height: '12.5%', left: `${cIdx * 12.5}%`, top: `${rIdx * 12.5}%`, zIndex: 9, display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', cursor: isCoach ? 'grab' : 'pointer', filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.5))', opacity: draggingPiece?.id === id ? 0.3 : 1, touchAction: 'none' }}>
                            {item === '⭐' ? <img src={STAR_ICON} style={{width:'80%', height:'80%', pointerEvents: 'none'}} alt="star" /> : <span style={{pointerEvents: 'none'}}>{item}</span>}
                        </div>
                    )
                })}

                {Object.entries(renderBoard).map(([id, piece]) => {
                    const cIdx = cols.indexOf(id[0]); const rIdx = rows.indexOf(parseInt(id[1]));
                    if (!hidePieces || role === 'COACH') {
                        let canInteract = false;
                        if (role === 'COACH' && !isDrawMode && !quickTool) canInteract = true;
                        else if (activeQuiz?.active) canInteract = true;
                        else if (!isFrozen || allowIllegal || freeMovement) canInteract = true;
                        else if (boardControl[userEmail] === 'both' || boardControl[userEmail] === piece[0]) canInteract = true;
                        
                        return (
                            <img key={id} src={(PIECE_SETS[pieceTheme]?.[piece] || PIECE_IMAGES[piece])} alt={piece} draggable={false} 
                                data-square={id} 
                                onPointerDown={(e) => {
                                    if (role === 'COACH' && (e.button === 2 || isDrawMode)) { e.preventDefault(); setDrawStartSq(id); } 
                                    else if (canInteract && !quickTool) { 
                                        e.preventDefault(); handleClick(id); setDraggingPiece({ type: 'board', id, item: piece, x: e.clientX, y: e.clientY }); 
                                    } else if (!quickTool) {
                                        e.preventDefault(); handleClick(id); 
                                    }
                                }}
                                style={{ position: 'absolute', width: '12.5%', height: '12.5%', left: `${cIdx * 12.5}%`, top: `${rIdx * 12.5}%`, cursor: canInteract ? 'grab' : 'pointer', zIndex: 10, touchAction: 'none', opacity: draggingPiece?.id === id ? 0.3 : 1 }} />
                        );
                    }
                    return null;
                })}

                <svg viewBox="0 0 100 100" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 20 }}>
                    {arrows.map(arr => {
                        const [from, to, colorNameRaw] = arr.split('-');
                        const colorName = colorNameRaw || 'green';
                        const solidColor = SOLID_COLOR_MAP[colorName] || SOLID_COLOR_MAP.green;
                        
                        const p1 = getSquareCoords(from, isFlipped);
                        const p2 = getSquareCoords(to, isFlipped);
                        if (!p1 || !p2) return null;

                        const dx = p2.cx - p1.cx;
                        const dy = p2.cy - p1.cy;
                        const len = Math.sqrt(dx * dx + dy * dy);
                        if (len === 0) return null;

                        const angle = Math.atan2(dy, dx);
                        
                        // Slim Lichess-style Arrowhead and Line sizing parameters
                        const headSize = 3.6; 
                        const headHalfWidth = 1.6;
                        
                        const tailOffset = 1.0;
                        const headOffset = headSize - 0.8; // merge overlap so line joins arrowhead seamlessly
                        
                        const x1Short = p1.cx + Math.cos(angle) * tailOffset;
                        const y1Short = p1.cy + Math.sin(angle) * tailOffset;
                        
                        const x2Short = p2.cx - Math.cos(angle) * headOffset;
                        const y2Short = p2.cy - Math.sin(angle) * headOffset;
                        
                        // Tip points to destination center
                        const tipX = p2.cx - Math.cos(angle) * 1.0;
                        const tipY = p2.cy - Math.sin(angle) * 1.0;
                        
                        // Base center
                        const baseX = tipX - Math.cos(angle) * headSize;
                        const baseY = tipY - Math.sin(angle) * headSize;
                        
                        // Base corners (perpendicular)
                        const leftX = baseX - Math.sin(angle) * headHalfWidth;
                        const leftY = baseY + Math.cos(angle) * headHalfWidth;
                        
                        const rightX = baseX + Math.sin(angle) * headHalfWidth;
                        const rightY = baseY - Math.cos(angle) * headHalfWidth;
                        
                        return (
                            <g key={arr} opacity="0.65">
                                <line 
                                    x1={x1Short} 
                                    y1={y1Short} 
                                    x2={x2Short} 
                                    y2={y2Short} 
                                    stroke={solidColor} 
                                    strokeWidth="1.8" 
                                    strokeLinecap="round" 
                                />
                                <polygon 
                                    points={`${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}`} 
                                    fill={solidColor}
                                    stroke={solidColor}
                                    strokeWidth="0.8"
                                    strokeLinejoin="round"
                                />
                            </g>
                        );
                    })}
                </svg>
            </div>

            {/* 4 Edge Drag Handles for ChessBoard */}
            <div className="edge-resize-handle hide-on-mobile" onPointerDown={startResizeFromEdge('top')} onTouchStart={startResizeFromEdge('top')} style={{ position: 'absolute', top: '-6px', left: 0, right: 0, height: '12px', cursor: 'ns-resize', zIndex: 100 }} title="Drag top edge to resize board" />
            <div className="edge-resize-handle hide-on-mobile" onPointerDown={startResizeFromEdge('bottom')} onTouchStart={startResizeFromEdge('bottom')} style={{ position: 'absolute', bottom: '-6px', left: 0, right: 0, height: '12px', cursor: 'ns-resize', zIndex: 100 }} title="Drag bottom edge to resize board" />
            <div className="edge-resize-handle hide-on-mobile" onPointerDown={startResizeFromEdge('left')} onTouchStart={startResizeFromEdge('left')} style={{ position: 'absolute', left: '-6px', top: 0, bottom: 0, width: '12px', cursor: 'ew-resize', zIndex: 100 }} title="Drag left edge to resize board" />
            <div className="edge-resize-handle hide-on-mobile" onPointerDown={startResizeFromEdge('right')} onTouchStart={startResizeFromEdge('right')} style={{ position: 'absolute', right: '-6px', top: 0, bottom: 0, width: '12px', cursor: 'ew-resize', zIndex: 100 }} title="Drag right edge to resize board" />
            <div
                className="resize-handle hide-on-mobile"
                onPointerDown={startResize}
                onTouchStart={startResize}
                title="Drag to resize board"
                style={{position: 'absolute', bottom: '-10px', right: '-10px', width: '20px', height: '20px', cursor: 'nwse-resize', zIndex: 100, background: 'linear-gradient(135deg, transparent 50%, var(--text-secondary) 50%)', borderRadius: '0 0 4px 0' }}
            />
        </div>

        {/* STUDENT CONTROLS BAR */}
        {role === 'STUDENT' && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', alignItems: 'center', marginTop: '5px', width: '100%' }}>
                <div className="neu-board-container">
                    <button onClick={() => setIsFlipped(!isFlipped)} className="neu-btn-circle" title="Flip Board">🔃</button>
                    <div style={{ width: '1.5px', background: 'rgba(163, 177, 198, 0.4)', height: '18px' }}></div>
                    
                    <select value={boardTheme} onChange={(e) => handleBoardThemeChange(e.target.value)} className="neu-select" title="Board Design">
                        <option value="green">🌿 Green</option>
                        <option value="wood">🪵 Wood</option>
                        <option value="blue">🌊 Blue</option>
                        <option value="sand">⛱️ Sand</option>
                        <option value="canvas">🎨 Canvas</option>
                        <option value="maple">🍁 Maple</option>
                        <option value="ic">❄️ Icy</option>
                        <option value="grey">🩶 Grey</option>
                        <option value="metal">🪙 Metal</option>
                        <option value="olive">🫒 Olive</option>
                        <option value="purple">🔮 Purple</option>
                        <option value="pink">🌸 Pink</option>
                        <option value="horn">📯 Horn</option>
                        <option value="newspaper">📰 Newspaper</option>
                    </select>

                    <select value={pieceTheme} onChange={(e) => handlePieceThemeChange(e.target.value)} className="neu-select" title="Pieces Design">
                        <option value="cburnett">♟️ Classic</option>
                        <option value="merida">♟️ Merida</option>
                        <option value="alpha">♟️ Alpha</option>
                        <option value="maestro">♟️ Maestro</option>
                        <option value="staunty">♟️ Staunty</option>
                        <option value="companion">♟️ Companion</option>
                        <option value="horsey">🐴 Horsey</option>
                    </select>

                    <button onClick={() => setStudentDots(!studentDots)} className={`neu-btn-circle ${studentDots ? 'active' : ''}`} title="Toggles Move Assist Dots">✨</button>
                </div>
            </div>
        )}

        {/* Primary Nav Bar for Coach Only */}
        {role === 'COACH' && (() => {
            const normTreeNav = normalizeTree(gameTree);
            const currNodeNav = normTreeNav.nodes[normTreeNav.currentNodeId];
            const isAtStart = !currNodeNav || currNodeNav.id === normTreeNav.rootId;
            const isAtEnd = !currNodeNav || !currNodeNav.children || currNodeNav.children.length === 0;

            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', width: '100%', flexWrap: 'wrap', marginTop: '5px' }}>
                    <div className="neu-board-container">
                        <button onClick={fastBackward} disabled={isAtStart} className="neu-btn-circle" title="First Move">⏮</button>
                        <button onClick={stepBackward} disabled={isAtStart} className="neu-btn-circle" title="Previous Move">◀</button>
                        <button onClick={stepForward} disabled={isAtEnd} className="neu-btn-circle" title="Next Move">▶</button>
                        <button onClick={fastForward} disabled={isAtEnd} className="neu-btn-circle" title="Last Move">⏭</button>
                        <button onClick={stepBackward} disabled={isAtStart} className="neu-btn-circle" title="Undo Last Move">⟲</button>
                        <button onClick={toggleStrictTurns} className={`neu-btn-circle ${strictTurns ? 'active' : ''}`} title={strictTurns ? "Strict Turns: ON" : "Continuous Moves: ON"}>⇅</button>
                        <button onClick={clearHighlightsAndArrows} className="neu-btn-circle" title="Clear Drawings">ⓧ</button>
                    </div>
                    <span 
                        onClick={() => setShowToolsPanel(!showToolsPanel)} 
                        style={{ 
                            cursor: 'pointer', 
                            color: 'var(--text-primary)', 
                            fontWeight: '700', 
                            fontSize: '12px', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px', 
                            textDecoration: 'underline',
                            userSelect: 'none'
                        }}
                    >
                        {showToolsPanel ? '👁️ Hide tools' : '👁️ Show tools'}
                    </span>
                    {hasSidebar && (
                        <span
                            onClick={() => setShowPalette(p => !p)}
                            style={{
                                cursor: 'pointer',
                                color: showPalette ? '#3b82f6' : 'var(--text-secondary)',
                                fontWeight: '700',
                                fontSize: '12px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                textDecoration: 'underline',
                                userSelect: 'none',
                                transition: 'color 0.2s'
                            }}
                            title={showPalette ? 'Hide pieces palette' : 'Show pieces palette'}
                        >
                            {showPalette ? '🎮 Hide palette' : '🎮 Show palette'}
                        </span>
                    )}
                </div>
            );
        })()}

        {/* COACH Library Playlist Navigation Panel */}
        {role === 'COACH' && classData?.playlistItems && classData.playlistItems.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '8px', marginTop: '8px' }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    width: '100%', 
                    background: 'rgba(245, 158, 11, 0.08)', 
                    padding: '8px 12px', 
                    borderRadius: '12px', 
                    border: '1.5px solid rgba(245, 158, 11, 0.25)',
                    boxSizing: 'border-box' 
                }}>
                    {/* Left balance placeholder */}
                    <div style={{ width: '32px', flexShrink: 0 }} />

                    {/* Centered playlist controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flex: 1 }}>
                        <button 
                            onClick={handlePlaylistPrev} 
                            disabled={classData.playlistIndex === 0} 
                            className="neu-btn-circle" 
                            style={{ background: classData.playlistIndex === 0 ? 'rgba(163, 177, 198, 0.2)' : '#f59e0b', color: classData.playlistIndex === 0 ? 'var(--text-secondary)' : 'black', cursor: classData.playlistIndex === 0 ? 'not-allowed' : 'pointer' }}
                            title="Previous Library Position"
                        >
                            ⏮
                        </button>
                        <div style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 'bold', minWidth: '150px', textAlign: 'center' }}>
                            📚 {classData.playlistItems[classData.playlistIndex]?.title || 'Position'} ({classData.playlistIndex + 1}/{classData.playlistItems.length})
                        </div>
                        <button 
                            onClick={handlePlaylistNext} 
                            disabled={classData.playlistIndex === classData.playlistItems.length - 1} 
                            className="neu-btn-circle" 
                            style={{ background: classData.playlistIndex === classData.playlistItems.length - 1 ? 'rgba(163, 177, 198, 0.2)' : '#f59e0b', color: classData.playlistIndex === classData.playlistItems.length - 1 ? 'var(--text-secondary)' : 'black', cursor: classData.playlistIndex === classData.playlistItems.length - 1 ? 'not-allowed' : 'pointer' }}
                            title="Next Library Position"
                        >
                            ⏭
                        </button>
                        
                        {classData.playlistItems[classData.playlistIndex]?.type === 'puzzle' && (
                            <button 
                                onClick={handleLaunchPlaylistQuiz} 
                                style={{ 
                                    padding: '6px 12px', 
                                    background: '#10b981', 
                                    color: 'black', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    fontWeight: 'bold', 
                                    fontSize: '11px', 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    boxShadow: '0 2px 5px rgba(16, 185, 129, 0.2)'
                                }}
                            >
                                🎯 Launch Quiz
                            </button>
                        )}
                    </div>

                    {/* Right dismiss button */}
                    <button 
                        onClick={handleClearPlaylist}
                        className="neu-btn-circle" 
                        style={{ 
                            width: '32px', 
                            height: '32px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            background: 'rgba(239, 68, 68, 0.1)', 
                            color: '#ef4444', 
                            border: '1px solid #ef4444', 
                            cursor: 'pointer', 
                            flexShrink: 0, 
                            padding: 0 
                        }}
                        title="Dismiss Library Playlist"
                    >
                        ❌
                    </button>
                </div>
                {classData.playlistItems[classData.playlistIndex]?.comments && (
                    <div style={{
                        background: 'rgba(16, 185, 129, 0.08)',
                        padding: '10px 15px',
                        borderRadius: '10px',
                        border: '1.5px dashed rgba(16, 185, 129, 0.3)',
                        fontSize: '12px',
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        boxSizing: 'border-box',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '14px' }}>💡</span>
                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>Solution / PGN Notes:</span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-primary)', lineHeight: '1.4' }}>
                            {classData.playlistItems[classData.playlistIndex].comments}
                        </div>
                    </div>
                )}
            </div>
        )}



        {/* COACH Sleek Neumorphic Secondary Tools Panel */}
        {role === 'COACH' && showToolsPanel && (
            <div className="neu-panel-inset">
                <button onClick={() => {setIsDrawMode(!isDrawMode); setQuickTool(null); setDrawStartSq(null);}} className={`neu-btn-square ${isDrawMode ? 'active' : ''}`} title="Draw Arrow/Highlight Mode (Right-click drag)">
                    🖍️
                </button>
                <button onClick={() => setBoardWidth(prev => prev === 520 ? 650 : 520)} className={`neu-btn-square ${boardWidth > 550 ? 'active' : ''}`} title="Resize Board Size">
                    ↗
                </button>
                <button onClick={handleClearBoard} className="neu-btn-square" title="Clear Board Grid">
                    🗑️
                </button>
                <button onClick={() => setDropToDelete(p => !p)} className={`neu-btn-square ${dropToDelete ? 'active' : ''}`} title={dropToDelete ? 'Drop-to-Delete: ON — drag pieces off board to remove' : 'Drop-to-Delete: OFF — click to enable'}>
                    🗂️
                </button>
                <button onClick={() => setShowCoords(!showCoords)} className={`neu-btn-square ${showCoords ? 'active' : ''}`} title="Toggle Board Coordinates">
                    🔢
                </button>
                <button onClick={toggleHidePieces} className={`neu-btn-square ${hidePieces ? 'active' : ''}`} title="Hide Chess Pieces (Blindfold Mode)">
                    👁️
                </button>
                <button onClick={openSetupModal} className={`neu-btn-square ${showSetup ? 'active' : ''}`} title="Setup Position">
                    🎛️
                </button>
                <button onClick={toggleMoveAssist} className={`neu-btn-square ${moveAssist ? 'active' : ''}`} title="Show Move Assist Dots">
                    ✨
                </button>
                <button onClick={copyPosition} className="neu-btn-square" title="Copy Board FEN">
                    🗎
                </button>
                <button onClick={openFenModal} className="neu-btn-square" title="Paste FEN">
                    📥
                </button>
                <button onClick={openPgnModal} className="neu-btn-square" title="Paste PGN (Load Game)">
                    📖
                </button>
                <button onClick={resetBoard} className="neu-btn-square" title="Reset Board to Starting Position">
                    🔄
                </button>
                <select 
                    onChange={(e) => {
                        if (e.target.value) {
                            loadPreset(e.target.value);
                            e.target.value = ''; // reset selection
                        }
                    }}
                    style={{ 
                        padding: '6px 10px', 
                        background: '#e0e5ec', 
                        border: '1px solid rgba(255, 255, 255, 0.45)', 
                        borderRadius: '8px', 
                        color: 'var(--text-primary)', 
                        fontSize: '12px', 
                        outline: 'none', 
                        cursor: 'pointer', 
                        boxShadow: 'var(--neu-shadow)', 
                        fontWeight: 'bold',
                        fontFamily: 'inherit'
                    }}
                    title="Load Endgame Presets"
                >
                    <option value="">🎯 Presets</option>
                    <option value="standard">♟️ Starting Board</option>
                    <option value="empty">📭 Empty Board</option>
                    <option value="kp">👑 King & Pawn</option>
                    <option value="kr">🏰 King & Rook</option>
                    <option value="kq">👑 King & Queen</option>
                    <option value="kbb">♗ King & 2 Bishops</option>
                </select>
                <button onClick={toggleIllegalMoves} className={`neu-btn-square ${allowIllegal ? 'active' : ''}`} title="Allow Illegal Moves">
                    🚫
                </button>
                {(role === 'COACH' || !classroomId) && (
                    <button onClick={toggleSquareCoords} className={`neu-btn-square ${showSquareCoords ? 'active' : ''}`} title="Toggle 64 Square Names">
                        🏷️
                    </button>
                )}
                {(role === 'COACH' || !classroomId) && (
                    <button onClick={toggleFreeMovement} className={`neu-btn-square ${freeMovement ? 'active' : ''}`} title="Allow Free Movements (Manual Board)">
                        🖐️
                    </button>
                )}
                <button onClick={playEngineMove} className="neu-btn-square" title="Play Engine Best Move">
                    🤖
                </button>
                <button onClick={openQuizModal} className="neu-btn-square" title="Launch Puzzle/Quiz">
                    ❓
                </button>
                <button onClick={sendEmoji} className="neu-btn-square" title="Send Random Floating Emoji">
                    😀
                </button>
                <button onClick={toggleRecording} className={`neu-btn-square ${isRecording ? 'active' : ''}`} title={isRecording ? "Stop Screen Recording" : "Start Screen Recording"}>
                    📹
                </button>
                {redoStack.length > 0 && (
                    <button onClick={eraseNotation} className="neu-btn-square" title="Cut notation history">
                        ✂️
                    </button>
                )}
            </div>
        )}

        {/* COACH Sleek Whiteboard Utilities Strip (Draw Colors & Target Items) */}
        {role === 'COACH' && showToolsPanel && (
            <div className="neu-panel-inset" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', padding: '10px 15px', marginBottom: '8px', flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
                 {/* Drawing Colors */}
                 <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>✏️ Draw:</span>
                      {['green', 'red', 'blue', 'yellow', 'orange'].map(color => {
                          const isSelected = selectedColor === color;
                          const displayColor = color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'blue' ? '#3b82f6' : color === 'yellow' ? '#eab308' : '#f97316';
                          return (
                              <button
                                  key={color}
                                  onClick={() => {
                                      setSelectedColor(color);
                                      setIsDrawMode(true);
                                  }}
                                  style={{
                                      width: '22px',
                                      height: '22px',
                                      borderRadius: '50%',
                                      background: displayColor,
                                      border: isSelected ? '2px solid var(--text-primary)' : '1px solid rgba(255,255,255,0.8)',
                                      boxShadow: isSelected ? 'inset 0 0 4px rgba(0,0,0,0.5)' : 'var(--neu-shadow)',
                                      cursor: 'pointer',
                                      transition: 'all 0.15s'
                                  }}
                                  title={`Select ${color} drawing tool`}
                              />
                          );
                      })}
                      <button
                          onClick={() => {
                              setSelectedColor(null);
                              setIsDrawMode(false);
                          }}
                          style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '6px',
                              background: !selectedColor ? '#3b82f6' : '#e0e5ec',
                              color: !selectedColor ? 'white' : 'var(--text-primary)',
                              border: '1px solid rgba(255,255,255,0.6)',
                              boxShadow: !selectedColor ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                              cursor: 'pointer',
                              fontSize: '11px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                          }}
                          title="Keyboard Modifiers Mode"
                      >
                          ⌨️
                      </button>
                      <button
                          onClick={clearHighlightsAndArrows}
                          style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '6px',
                              background: '#e0e5ec',
                              border: '1px solid rgba(255,255,255,0.6)',
                              boxShadow: 'var(--neu-shadow)',
                              cursor: 'pointer',
                              fontSize: '11px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                          }}
                          title="Clear Drawings"
                      >
                          🧹
                      </button>
                 </div>

                 <div style={{ width: '1.5px', background: 'rgba(163, 177, 198, 0.4)', height: '20px' }}></div>

                 {/* Cute Items */}
                 <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>🍎 Target Items:</span>
                      {CUTE_ITEMS.map(item => (
                          <div 
                              key={item} 
                              onPointerDown={(e) => { 
                                  e.preventDefault(); 
                                  setQuickTool(item); 
                                  setIsDrawMode(false); 
                                  setDraggingPiece({ type: 'palette', item, x: e.clientX, y: e.clientY }); 
                              }} 
                              style={{ 
                                  width: '24px', 
                                  height: '24px', 
                                  cursor: 'pointer', 
                                  background: quickTool === item ? 'rgba(245, 158, 11, 0.2)' : 'var(--bg-primary)', 
                                  borderRadius: '6px', 
                                  display: 'flex', 
                                  justifyContent: 'center', 
                                  alignItems: 'center', 
                                  border: quickTool === item ? '2px solid #f59e0b' : 'none', 
                                  fontSize: '14px', 
                                  touchAction: 'none', 
                                  boxShadow: 'var(--neu-shadow)', 
                                  transition: 'all 0.15s ease' 
                              }}
                          >
                              {item}
                          </div>
                      ))}
                 </div>
            </div>
        )}



        {activeQuiz?.active && (
            <div className="glass-panel" style={{ marginTop: '10px', padding: '15px', background: 'var(--bg-primary)', border: '1.5px solid rgba(245, 158, 11, 0.4)', borderRadius: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#f59e0b', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>🎯</span> Puzzle Progress & Attempts
                </h4>
                
                {role === 'STUDENT' ? (
                    <div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                            Question: <strong style={{ color: 'var(--text-primary)' }}>{activeQuiz.question}</strong> (Value: {activeQuiz.points} XP)
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {(!classData?.quizAttempts?.[userEmail.replace(/\./g, '_')] || classData.quizAttempts[userEmail.replace(/\./g, '_')].length === 0) ? (
                                <span style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--text-secondary)' }}>No attempts made yet. Make a move on the board to solve!</span>
                            ) : (
                                classData.quizAttempts[userEmail.replace(/\./g, '_')].map((att, idx) => (
                                    <span key={idx} style={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        gap: '4px', 
                                        fontSize: '11px', 
                                        padding: '4px 8px', 
                                        background: 'var(--bg-primary)', 
                                        borderRadius: '6px', 
                                        boxShadow: 'var(--neu-shadow)', 
                                        border: att.correct ? '1px solid #10b981' : '1px solid #ef4444',
                                        color: att.correct ? '#10b981' : '#ef4444',
                                        fontWeight: 'bold'
                                    }}>
                                        {idx + 1}. {att.move} {att.correct ? '✅' : '❌'}
                                    </span>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                        {(!classData?.students || classData.students.length === 0) ? (
                            <span style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--text-secondary)' }}>No students in class.</span>
                        ) : (
                            classData.students.map(email => {
                                const safeEmail = email.replace(/\./g, '_');
                                const attempts = classData.quizAttempts?.[safeEmail] || [];
                                const solved = activeQuiz.solvedBy?.includes(email);
                                return (
                                    <div key={email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.2)', fontSize: '11px' }}>
                                        <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                            {email.split('@')[0]}: {solved ? <span style={{ color: '#10b981' }}>Solved ✅</span> : <span style={{ color: '#f59e0b' }}>Solving... ⏳</span>}
                                        </span>
                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                            {attempts.length === 0 ? (
                                                <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '10px' }}>No moves tried</span>
                                            ) : (
                                                attempts.map((att, idx) => (
                                                    <span key={idx} style={{ 
                                                        fontSize: '10px', 
                                                        padding: '2px 5px', 
                                                        borderRadius: '4px', 
                                                        background: 'var(--bg-primary)', 
                                                        boxShadow: 'var(--neu-shadow-inset)',
                                                        color: att.correct ? '#10b981' : '#ef4444',
                                                        border: att.correct ? '1.5px solid #10b981' : '1.5px solid #ef4444',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {att.move} {att.correct ? '✅' : '❌'}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        )}

        {/* Right-Click Context Menu for Move Annotations & Comments */}
        {contextMenu && (
            <div 
                style={{ 
                    position: 'fixed', 
                    top: Math.min(contextMenu.y, window.innerHeight - 240), 
                    left: Math.min(contextMenu.x, window.innerWidth - 280), 
                    zIndex: 99999,
                    background: '#1e1e1e',
                    border: '1px solid #333',
                    borderRadius: '12px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
                    padding: '12px',
                    width: '260px',
                    color: '#e0e0e0',
                    fontSize: '12px'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #333' }}>
                    <strong style={{ color: '#3b82f6' }}>Move {contextMenu.moveIndex + 1}: {contextMenu.san}</strong>
                    <button onClick={() => setContextMenu(null)} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                </div>

                {/* NAG Symbols */}
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', fontWeight: 'bold' }}>NAG Symbols:</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {['!!', '!', '!?', '?!', '?', '??'].map(nag => (
                            <button
                                key={nag}
                                onClick={() => {
                                    handleAddNag(contextMenu.moveIndex, nag);
                                    setContextMenu(null);
                                }}
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #444',
                                    background: contextMenu.nag === nag ? '#3b82f6' : '#2a2a2a',
                                    color: nag.includes('!') ? '#34d399' : '#f87171',
                                    fontWeight: '900',
                                    cursor: 'pointer',
                                    fontSize: '11px'
                                }}
                            >
                                {nag}
                            </button>
                        ))}
                        <button
                            onClick={() => {
                                handleAddNag(contextMenu.moveIndex, null);
                                setContextMenu(null);
                            }}
                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#aaa', cursor: 'pointer', fontSize: '10px' }}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Comment Field */}
                <div>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', fontWeight: 'bold' }}>Add / Edit Comment:</div>
                    <textarea
                        rows={3}
                        value={contextCommentInput}
                        onChange={(e) => setContextCommentInput(e.target.value)}
                        placeholder="Write move commentary..."
                        style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            background: '#111',
                            border: '1px solid #444',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '6px',
                            fontSize: '11px',
                            resize: 'none',
                            outline: 'none'
                        }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '6px' }}>
                        <button
                            onClick={() => {
                                handleAddComment(contextMenu.moveIndex, contextCommentInput);
                                setContextMenu(null);
                            }}
                            style={{ padding: '4px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Moves history moved to right side panel Moves tab */}
    </div>
  );
}